import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import "react-data-table-component-extensions/dist/index.css";
import * as XLSX from "xlsx";
import { getApiBaseUrl } from "../../utils/utils";

export default function BillingOrders() {
  const API_URL = process.env.REACT_APP_API_URL;

  // All possible statuses - hardcoded list (always shown even with 0 count)
  const ALL_STATUSES = [
    "DELIVERED",
    "DESTINATION_LINEHAUL_DEPART",
    "DROP_ENROUTE",
    "FAILED",
    "INSCAN_AT_WAREHOUSE",
    "PICKED_UP",
    "PICKUP_ENROUTE",
    "READY",
    "RECEIVED",
    "RETURN_TO_ORIGIN",
    "RETURN_TO_SHIPPER",
  ];

  // Status color mapping - includes all possible statuses
  const getStatusColor = (status) => {
    const colors = {
      DELIVERED: "#10b981",
      PICKED_UP: "#3b82f6",
      FAILED: "#ef4444",
      ASSIGNED: "#f59e0b",
      PENDING: "#8b5cf6",
      RETURNED: "#6b7280",
      CANCELLED: "#dc2626",
      IN_TRANSIT: "#0ea5e9",
      OUT_FOR_DELIVERY: "#22c55e",
      RESCHEDULED: "#a855f7",
      ON_HOLD: "#eab308",
      RTO: "#f97316",
      DESTINATION_LINEHAUL_DEPART: "#06b6d4",
      DROP_ENROUTE: "#0891b2",
      INSCAN_AT_WAREHOUSE: "#7c3aed",
      PICKUP_ENROUTE: "#2563eb",
      READY: "#059669",
      RECEIVED: "#0d9488",
      RETURN_TO_ORIGIN: "#dc2626",
      RETURN_TO_SHIPPER: "#b91c1c",
      UNASSIGNED: "#9ca3af",
    };
    return colors[status] || "#6b7280";
  };

  // Filter State
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState(["DELIVERED"]);
  const [billedFilter, setBilledFilter] = useState("NOT_BILLED");
  const [selectedPartner, setSelectedPartner] = useState("0");
  const [includeAllOrders, setIncludeAllOrders] = useState(false);
  const [availableStatuses, setAvailableStatuses] = useState([]);

  // Data State
  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [partners, setPartners] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [billingList, setBillingList] = useState([]);

  // UI State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [mainView, setMainView] = useState("orders"); // "orders" | "billing" | "history"
  const [billedDate, setBilledDate] = useState("");
  const [billingCurrency, setBillingCurrency] = useState("AED");
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState([
    "awb", "customerName", "clientRef", "phone", "address", "city", "shipper", "shipperPhone", "billedAmount", "billedCurrency", "billedBy", "project", "serviceTime"
  ]);

  // Table search/filter state for performance
  const [tableSearchText, setTableSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const searchDebounceRef = useRef(null);

  // Bulk Update Modal State
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [bulkUpdateFile, setBulkUpdateFile] = useState(null);
  const [bulkUpdateFileHeaders, setBulkUpdateFileHeaders] = useState([]);
  const [bulkUpdateFileData, setBulkUpdateFileData] = useState([]);
  const [bulkUpdateMapping, setBulkUpdateMapping] = useState({
    awb: "",
    amount: "",
    currency: "",
  });
  const [bulkUpdateDefaultCurrency, setBulkUpdateDefaultCurrency] = useState("AED");
  const [bulkUpdateStep, setBulkUpdateStep] = useState("upload"); // "upload" | "mapping" | "preview" | "processing" | "results"
  const [bulkUpdateLoading, setBulkUpdateLoading] = useState(false);
  const [bulkUpdateResults, setBulkUpdateResults] = useState(null);
  const bulkUpdateFileInputRef = useRef(null);

  // Batch Task State - NEW
  const [batchTaskId, setBatchTaskId] = useState(null);
  const [batchTaskProgress, setBatchTaskProgress] = useState(null);
  const [batchTaskDetails, setBatchTaskDetails] = useState(null);
  const [batchTaskHistory, setBatchTaskHistory] = useState([]);
  const [batchHistoryLoading, setBatchHistoryLoading] = useState(false);
  const [batchDescription, setBatchDescription] = useState("");
  const [batchSendEmail, setBatchSendEmail] = useState(false);
  const batchPollingRef = useRef(null);

  // History Filter State
  const getDefaultFromDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  };
  const getDefaultToDate = () => new Date().toISOString().split('T')[0];
  
  const [historyFromDate, setHistoryFromDate] = useState(getDefaultFromDate());
  const [historyToDate, setHistoryToDate] = useState(getDefaultToDate());
  const [historyStatusFilter, setHistoryStatusFilter] = useState("");
  const [lastHistoryUpdate, setLastHistoryUpdate] = useState(null);
  const [batchCreating, setBatchCreating] = useState(false);
  const [batchCreateProgress, setBatchCreateProgress] = useState("");

  // Progress State for split fetch
  const [fetchProgress, setFetchProgress] = useState({
    isActive: false,
    isComplete: false,
    currentChunk: 0,
    totalChunks: 0,
    successCount: 0,
    failedCount: 0,
    currentFromDate: "",
    currentToDate: "",
    completedRanges: [],
    failedRanges: [],
    totalRecords: 0,
    startTime: null,
    avgTimePerChunk: 0,
  });

  // Ref to track current fetch operation and cancel previous ones
  const abortControllerRef = useRef(null);
  const fetchIdRef = useRef(0);
  const didInitialFetchRef = useRef(false);

  // Retry failed chunks
  const retryFailedChunks = async () => {
    if (fetchProgress.failedRanges.length === 0) return;

    // Cancel any ongoing fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;

    const failedChunks = [...fetchProgress.failedRanges];
    const retryStartTime = Date.now();
    
    setFetchProgress(prev => ({
      ...prev,
      isActive: true,
      isComplete: false,
      currentChunk: 0,
      totalChunks: prev.totalChunks,
      failedRanges: [],
      failedCount: 0,
      startTime: retryStartTime,
    }));

    // Build Map from existing orders for O(1) duplicate check
    const ordersMap = new Map();
    allOrders.forEach(order => {
      // Dedupe by the unique orderId (fall back to awb) so distinct rows aren't collapsed.
      const k = order.orderId != null ? 'O' + order.orderId : order.awb;
      if (k != null) {
        ordersMap.set(k, order);
      }
    });

    let successCount = fetchProgress.successCount;
    let failedCount = 0;
    const newCompletedRanges = [...fetchProgress.completedRanges];
    const newFailedRanges = [];
    let totalTime = 0;

    for (let i = 0; i < failedChunks.length; i++) {
      // Check if cancelled
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }

      const chunk = failedChunks[i];
      const chunkStartTime = Date.now();
      
      setFetchProgress(prev => ({
        ...prev,
        currentChunk: prev.successCount + i + 1,
        currentFromDate: chunk.from,
        currentToDate: chunk.to,
      }));

      try {
        const response = await axios.get(
          `${API_URL}shypvdriverapp/jobs/getBillingOrders?fromDate=${chunk.from}&toDate=${chunk.to}&status=ALL`,
          { signal: abortController.signal }
        );

        if (currentFetchId !== fetchIdRef.current) {
          return;
        }

        const ordersData = response.data.data?.orders || [];
        
        // Add to Map for deduplication
        ordersData.forEach(order => {
          const k = order.orderId != null ? 'O' + order.orderId : order.awb;
          if (k != null && !ordersMap.has(k)) {
            ordersMap.set(k, order);
          }
        });

        successCount++;
        const chunkTime = Date.now() - chunkStartTime;
        totalTime += chunkTime;
        
        newCompletedRanges.push({ from: chunk.from, to: chunk.to, count: ordersData.length });

        const consolidatedOrders = Array.from(ordersMap.values());

        setAllOrders(consolidatedOrders);
        const visible = applyFiltersToOrders(consolidatedOrders);

        setFetchProgress(prev => ({
          ...prev,
          successCount,
          completedRanges: [...newCompletedRanges],
          totalRecords: visible.length,
          avgTimePerChunk: totalTime / (i + 1),
        }));

      } catch (err) {
        if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
          return;
        }
        if (currentFetchId !== fetchIdRef.current) {
          return;
        }

        console.error(`Retry failed for ${chunk.from} to ${chunk.to}:`, err);
        failedCount++;
        newFailedRanges.push({ from: chunk.from, to: chunk.to, error: err.message });

        setFetchProgress(prev => ({
          ...prev,
          failedCount,
          failedRanges: [...newFailedRanges],
        }));
      }

      if (i < failedChunks.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    if (currentFetchId !== fetchIdRef.current) {
      return;
    }

    const finalOrders = Array.from(ordersMap.values());
    updateStatusOptions(finalOrders);

    setFetchProgress(prev => ({
      ...prev,
      isActive: false,
      isComplete: true,
    }));
  };

  // Styles - Modern responsive UI like NewMedicalTaskCreation
  const styles = {
    container: {
      padding: "24px",
      backgroundColor: "#f5f7fa",
      minHeight: "100vh",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
      flexWrap: "wrap",
      gap: "16px",
    },
    title: {
      fontSize: "22px",
      fontWeight: "600",
      color: "#2d3748",
      margin: 0,
    },
    card: {
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      marginBottom: "20px",
      border: "1px solid #e8ecf1",
    },
    cardHeader: {
      padding: "16px 20px",
      borderBottom: "1px solid #e8ecf1",
      backgroundColor: "#fafbfc",
      borderRadius: "12px 12px 0 0",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    cardTitle: {
      fontSize: "15px",
      fontWeight: "600",
      color: "#374151",
      margin: 0,
    },
    cardBody: {
      padding: "20px",
    },
    formRow: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
      gap: "16px",
      marginBottom: "16px",
    },
    formGroup: {
      marginBottom: "0",
    },
    label: {
      display: "block",
      fontSize: "13px",
      fontWeight: "500",
      color: "#4b5563",
      marginBottom: "8px",
    },
    input: {
      width: "100%",
      padding: "10px 14px",
      fontSize: "14px",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      backgroundColor: "#fff",
      transition: "border-color 0.2s",
      outline: "none",
      boxSizing: "border-box",
    },
    select: {
      width: "100%",
      padding: "10px 14px",
      fontSize: "14px",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      backgroundColor: "#fff",
      cursor: "pointer",
      outline: "none",
      boxSizing: "border-box",
    },
    btnPrimary: {
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "500",
      color: "#fff",
      backgroundColor: "#3b82f6",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      transition: "background-color 0.2s",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
    },
    btnSuccess: {
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "500",
      color: "#fff",
      backgroundColor: "#10b981",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
    },
    btnSecondary: {
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "500",
      color: "#4b5563",
      backgroundColor: "#f3f4f6",
      border: "1px solid #d1d5db",
      borderRadius: "8px",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
    },
    btnDanger: {
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: "500",
      color: "#fff",
      backgroundColor: "#ef4444",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
      gap: "16px",
      marginBottom: "20px",
    },
    statCard: {
      backgroundColor: "#fff",
      borderRadius: "12px",
      padding: "20px",
      textAlign: "center",
      border: "1px solid #e8ecf1",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    },
    statValue: {
      fontSize: "28px",
      fontWeight: "700",
      marginBottom: "4px",
    },
    statLabel: {
      fontSize: "13px",
      color: "#6b7280",
    },
    toggleContainer: {
      display: "inline-flex",
      backgroundColor: "#f1f5f9",
      borderRadius: "14px",
      padding: "5px",
      marginBottom: "24px",
      border: "1px solid #e2e8f0",
    },
    toggleBtn: {
      padding: "12px 28px",
      fontSize: "14px",
      fontWeight: "500",
      border: "none",
      borderRadius: "10px",
      cursor: "pointer",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    toggleBtnActive: {
      backgroundColor: "#fff",
      color: "#3b82f6",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
    },
    toggleBtnInactive: {
      backgroundColor: "transparent",
      color: "#64748b",
    },
    badge: {
      display: "inline-block",
      padding: "4px 10px",
      fontSize: "12px",
      fontWeight: "500",
      borderRadius: "12px",
    },
    billingListItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12px 16px",
      backgroundColor: "#f8fafc",
      borderRadius: "8px",
      marginBottom: "8px",
      border: "1px solid #e2e8f0",
    },
    infoBanner: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "12px 16px",
      backgroundColor: "#eff6ff",
      borderRadius: "8px",
      marginBottom: "16px",
      border: "1px solid #bfdbfe",
      fontSize: "14px",
      color: "#1e40af",
      flexWrap: "wrap",
    },
    checkbox: {
      width: "18px",
      height: "18px",
      cursor: "pointer",
      accentColor: "#3b82f6",
    },
    checkboxLabel: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 14px",
      backgroundColor: "#f8fafc",
      borderRadius: "8px",
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: "500",
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "#e2e8f0",
      transition: "all 0.2s",
    },
    checkboxLabelActive: {
      backgroundColor: "#eff6ff",
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "#3b82f6",
      color: "#1e40af",
    },
    statusCheckboxGrid: {
      display: "flex",
      flexWrap: "wrap",
      gap: "8px",
    },
  };

  // CSS keyframes for animations
  useEffect(() => {
    const styleId = "billing-orders-animations";
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement("style");
      styleSheet.id = styleId;
      styleSheet.textContent = `
        @keyframes slideDownAlert {
          from { opacity: 0; transform: translateY(-30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(styleSheet);
    }
  }, []);

  // Auto-clear messages
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Debounced search handler
  const handleSearchChange = useCallback((text) => {
    setTableSearchText(text);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearchText(text);
    }, 300);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // Initialize dates
  useEffect(() => {
    const today = new Date();
    const toDateFormatted = today.toISOString().split("T")[0];
    const fromDateFormatted = new Date(today);
    // last 7 days (inclusive)
    fromDateFormatted.setDate(today.getDate() - 6);
    const fromDateStr = fromDateFormatted.toISOString().split("T")[0];

    setFromDate(fromDateStr);
    setToDate(toDateFormatted);
  }, []);

  const downloadExcel = () => {
    if (loading || orders.length === 0) return;

    const rows = flattenOrderData(orders);
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");

    const safeFrom = fromDate || "from";
    const safeTo = toDate || "to";
    XLSX.writeFile(wb, `billing_orders_${safeFrom}_to_${safeTo}.xlsx`);
  };

  // ===================== BULK UPDATE BILLING FUNCTIONS =====================

  // Download bulk update template
  const downloadBulkTemplate = () => {
    const templateData = [
      { UB: "AWB123456789", Amount: "100.00" },
      { UB: "AWB987654321", Amount: "250.50" },
    ];
    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "billing_upload_template.xlsx");
  };

  // Reset bulk update modal state
  const resetBulkUpdateModal = () => {
    stopProgressPolling();
    setShowBulkUpdateModal(false);
    setBulkUpdateFile(null);
    setBulkUpdateFileHeaders([]);
    setBulkUpdateFileData([]);
    setBulkUpdateMapping({ awb: "", amount: "", currency: "" });
    setBulkUpdateDefaultCurrency("AED");
    setBulkUpdateStep("upload");
    setBulkUpdateLoading(false);
    setBulkUpdateResults(null);
    setBatchTaskId(null);
    setBatchTaskProgress(null);
    setBatchTaskDetails(null);
    setBatchDescription("");
    setBatchSendEmail(false);
    if (bulkUpdateFileInputRef.current) {
      bulkUpdateFileInputRef.current.value = "";
    }
  };

  // Handle file selection for bulk update
  const handleBulkUpdateFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setBulkUpdateFile(file);
    setBulkUpdateLoading(true);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = event.target.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          setError("File must contain at least a header row and one data row.");
          setBulkUpdateLoading(false);
          return;
        }

        const headers = jsonData[0].map((h, idx) => ({
          index: idx,
          name: String(h || `Column ${idx + 1}`).trim(),
        }));

        const rows = jsonData.slice(1).filter((row) =>
          row.some((cell) => cell !== null && cell !== undefined && cell !== "")
        );

        setBulkUpdateFileHeaders(headers);
        setBulkUpdateFileData(rows);
        setBulkUpdateStep("mapping");

        // Auto-detect column mappings - prioritize UB for AWB
        const autoMapping = { awb: "", amount: "", currency: "" };
        headers.forEach((header, idx) => {
          const lowerName = header.name.toLowerCase().trim();
          const exactName = header.name.trim();
          
          // Priority 1: Exact "UB" match for AWB
          if (exactName === "UB" || exactName === "ub" || lowerName === "ub") {
            autoMapping.awb = header.index.toString();
          } 
          // Priority 2: Other AWB-like columns (only if UB not found)
          else if (
            !autoMapping.awb &&
            (lowerName.includes("awb") ||
            lowerName.includes("tracking") ||
            lowerName.includes("waybill"))
          ) {
            autoMapping.awb = header.index.toString();
          }
          
          // Amount detection
          if (
            lowerName.includes("amount") ||
            lowerName.includes("price") ||
            lowerName.includes("cost") ||
            lowerName.includes("bill")
          ) {
            autoMapping.amount = header.index.toString();
          }
          
          // Currency detection
          if (
            lowerName.includes("currency") ||
            lowerName.includes("curr")
          ) {
            autoMapping.currency = header.index.toString();
          }
        });
        setBulkUpdateMapping(autoMapping);
      } catch (err) {
        console.error("Error parsing file:", err);
        setError("Failed to parse file. Please ensure it's a valid CSV or Excel file.");
      } finally {
        setBulkUpdateLoading(false);
      }
    };

    reader.onerror = () => {
      setError("Failed to read file.");
      setBulkUpdateLoading(false);
    };

    reader.readAsBinaryString(file);
  };

  // Process bulk update - add items to billing list
  const processBulkUpdate = () => {
    // Validate mapping
    if (!bulkUpdateMapping.awb || !bulkUpdateMapping.amount) {
      setError("Please map at least AWB Number and Billed Amount columns.");
      return;
    }

    const awbIdx = parseInt(bulkUpdateMapping.awb);
    const amountIdx = parseInt(bulkUpdateMapping.amount);
    const currencyIdx = bulkUpdateMapping.currency
      ? parseInt(bulkUpdateMapping.currency)
      : null;

    // Build items array
    const newItems = [];
    const invalidRows = [];
    const duplicateRows = [];

    bulkUpdateFileData.forEach((row, rowIndex) => {
      const awb = row[awbIdx];
      const amount = row[amountIdx];
      // Use mapped currency column OR default currency
      const currency = currencyIdx !== null ? row[currencyIdx] : bulkUpdateDefaultCurrency;

      if (!awb || awb === "") {
        invalidRows.push({
          row: rowIndex + 2,
          awb: awb || "(empty)",
          error: "Missing AWB number",
        });
        return;
      }

      const parsedAmount = parseFloat(amount);
      if (isNaN(parsedAmount)) {
        invalidRows.push({
          row: rowIndex + 2,
          awb: String(awb),
          error: `Invalid amount: "${amount}"`,
        });
        return;
      }

      const awbStr = String(awb).trim();
      
      // Check if already in billing list
      if (billingList.some((item) => item.awb === awbStr)) {
        duplicateRows.push({
          row: rowIndex + 2,
          awb: awbStr,
          error: "Already in billing list",
        });
        return;
      }

      // Check if duplicate in current upload
      if (newItems.some((item) => item.awb === awbStr)) {
        duplicateRows.push({
          row: rowIndex + 2,
          awb: awbStr,
          error: "Duplicate in file",
        });
        return;
      }

      newItems.push({
        awb: awbStr,
        amount: parsedAmount.toFixed(2),
      });
    });

    if (newItems.length === 0 && invalidRows.length === 0 && duplicateRows.length === 0) {
      setError("No valid rows found in the file. Please check your data and mapping.");
      return;
    }

    // Add valid items to billing list
    if (newItems.length > 0) {
      setBillingList([...billingList, ...newItems]);
      // Also set the common currency to the one used in upload
      if (!bulkUpdateMapping.currency) {
        setBillingCurrency(bulkUpdateDefaultCurrency);
      }
    }

    // Show results
    setBulkUpdateResults({
      totalProcessed: bulkUpdateFileData.length,
      successCount: newItems.length,
      failedCount: invalidRows.length + duplicateRows.length,
      errors: [...invalidRows, ...duplicateRows],
    });
    setBulkUpdateStep("results");

    if (newItems.length > 0) {
      setSuccess(`Added ${newItems.length} item(s) to billing list.`);
    }
  };

  // Download error report for bulk update
  const downloadBulkUpdateErrorReport = () => {
    if (!bulkUpdateResults || !bulkUpdateResults.errors?.length) return;

    const errorData = bulkUpdateResults.errors.map((err) => ({
      "Row Number": err.row,
      "AWB Number": err.awb,
      "Error Message": err.error,
    }));

    const ws = XLSX.utils.json_to_sheet(errorData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Errors");

    const timestamp = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `bulk_update_errors_${timestamp}.xlsx`);
  };

  // ===================== BATCH TASK API FUNCTIONS =====================

  // Get user info from session storage
  const getUserInfo = () => ({
    userId: parseInt(sessionStorage.getItem("userid") || "0"),
    userName: sessionStorage.getItem("user_name") || sessionStorage.getItem("name") || "Unknown",
    userEmail: sessionStorage.getItem("useremail") || "",
  });

  // Create batch task
  const createBatchTask = async (billingItems) => {
    const userInfo = getUserInfo();
    
    // Build payload - only include required fields and non-empty optional fields
    const payload = {
      userId: userInfo.userId,
      userName: userInfo.userName,
      userEmail: userInfo.userEmail,
      billingItems: billingItems,
    };
    
    // Add optional fields only if they have values
    if (batchDescription) {
      payload.description = batchDescription;
    }
    if (bulkUpdateFile?.name) {
      payload.sourceFileName = bulkUpdateFile.name;
    }
    if (batchSendEmail && userInfo.userEmail) {
      payload.sendNotificationEmail = true;
      payload.notificationEmail = userInfo.userEmail;
    }
    
    try {
      console.log("Creating batch task with payload:", JSON.stringify(payload, null, 2));
      const response = await axios.post(
        `${API_URL}shypvdriverapp/finance/billing/batch/create`,
        payload
      );

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || "Failed to create batch task");
      }
    } catch (err) {
      console.error("Error creating batch task:", err);
      throw err;
    }
  };

  // Start batch processing
  const startBatchProcessing = async (taskId) => {
    try {
      const response = await axios.post(
        `${API_URL}shypvdriverapp/finance/billing/batch/${taskId}/start`
      );
      return response.data;
    } catch (err) {
      console.error("Error starting batch processing:", err);
      throw err;
    }
  };

  // Get batch progress
  const getBatchProgress = async (taskId) => {
    try {
      const response = await axios.get(
        `${API_URL}shypvdriverapp/finance/billing/batch/${taskId}/progress`
      );
      return response.data.data;
    } catch (err) {
      console.error("Error getting batch progress:", err);
      throw err;
    }
  };

  // Get batch task details
  const getBatchDetails = async (taskId) => {
    try {
      const response = await axios.get(
        `${API_URL}shypvdriverapp/finance/billing/batch/${taskId}/details`
      );
      return response.data.data;
    } catch (err) {
      console.error("Error getting batch details:", err);
      throw err;
    }
  };

  // Pause batch processing
  const pauseBatchProcessing = async (taskId) => {
    try {
      const response = await axios.post(
        `${API_URL}shypvdriverapp/finance/billing/batch/${taskId}/pause`
      );
      if (response.data.success) {
        setBatchTaskProgress(response.data.data);
      }
      return response.data;
    } catch (err) {
      console.error("Error pausing batch:", err);
      throw err;
    }
  };

  // Resume batch processing
  const resumeBatchProcessing = async (taskId) => {
    try {
      const response = await axios.post(
        `${API_URL}shypvdriverapp/finance/billing/batch/${taskId}/resume`
      );
      if (response.data.success) {
        setBatchTaskProgress(response.data.data);
        startProgressPolling(taskId);
      }
      return response.data;
    } catch (err) {
      console.error("Error resuming batch:", err);
      throw err;
    }
  };

  // Cancel batch processing
  const cancelBatchProcessing = async (taskId) => {
    try {
      const response = await axios.post(
        `${API_URL}shypvdriverapp/finance/billing/batch/${taskId}/cancel`
      );
      if (response.data.success) {
        stopProgressPolling();
        setBatchTaskProgress(response.data.data);
      }
      return response.data;
    } catch (err) {
      console.error("Error cancelling batch:", err);
      throw err;
    }
  };

  // Retry all failed items
  const retryAllFailedItems = async (taskId) => {
    try {
      const response = await axios.post(
        `${API_URL}shypvdriverapp/finance/billing/batch/${taskId}/retry`
      );
      if (response.data.success) {
        setBatchTaskProgress(response.data.data);
        startProgressPolling(taskId);
      }
      return response.data;
    } catch (err) {
      console.error("Error retrying failed items:", err);
      throw err;
    }
  };

  // Retry single item
  const retrySingleItem = async (itemId) => {
    try {
      const response = await axios.post(
        `${API_URL}shypvdriverapp/finance/billing/batch/items/${itemId}/retry`
      );
      return response.data;
    } catch (err) {
      console.error("Error retrying item:", err);
      throw err;
    }
  };

  // Get user's batch task history
  const fetchBatchHistory = async (fromDateParam, toDateParam, statusParam) => {
    const userInfo = getUserInfo();
    setBatchHistoryLoading(true);
    
    try {
      let url = `${API_URL}shypvdriverapp/finance/billing/batch/user/${userInfo.userId}`;
      const params = new URLSearchParams();
      if (fromDateParam || historyFromDate) params.append("fromDate", fromDateParam || historyFromDate);
      if (toDateParam || historyToDate) params.append("toDate", toDateParam || historyToDate);
      if (statusParam || historyStatusFilter) params.append("status", statusParam || historyStatusFilter);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url);
      if (response.data.success) {
        setBatchTaskHistory(response.data.data || []);
        setLastHistoryUpdate(new Date());
      }
    } catch (err) {
      console.error("Error fetching batch history:", err);
      setError("Failed to fetch batch history");
    } finally {
      setBatchHistoryLoading(false);
    }
  };

  // Start progress polling
  const startProgressPolling = (taskId) => {
    stopProgressPolling(); // Clear any existing polling
    
    batchPollingRef.current = setInterval(async () => {
      try {
        const progress = await getBatchProgress(taskId);
        setBatchTaskProgress(progress);

        // Stop polling when task reaches final state
        const finalStatuses = ["COMPLETED", "FAILED", "PARTIALLY_COMPLETED", "CANCELLED"];
        if (finalStatuses.includes(progress.status)) {
          stopProgressPolling();
          // Fetch full details when complete
          const details = await getBatchDetails(taskId);
          setBatchTaskDetails(details);
          setBulkUpdateStep("results");
        }
      } catch (err) {
        console.error("Error polling progress:", err);
      }
    }, 2000);
  };

  // Stop progress polling
  const stopProgressPolling = () => {
    if (batchPollingRef.current) {
      clearInterval(batchPollingRef.current);
      batchPollingRef.current = null;
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopProgressPolling();
  }, []);

  // Process batch update - submit to API
  const processBatchUpdate = async () => {
    // Validate mapping
    if (!bulkUpdateMapping.awb || !bulkUpdateMapping.amount) {
      setError("Please map at least AWB Number and Billed Amount columns.");
      return;
    }

    const awbIdx = parseInt(bulkUpdateMapping.awb);
    const amountIdx = parseInt(bulkUpdateMapping.amount);
    const currencyIdx = bulkUpdateMapping.currency
      ? parseInt(bulkUpdateMapping.currency)
      : null;

    // Build billing items array
    const billingItems = [];
    const invalidRows = [];

    bulkUpdateFileData.forEach((row, rowIndex) => {
      const awb = row[awbIdx];
      const amount = row[amountIdx];
      const currency = currencyIdx !== null ? row[currencyIdx] : bulkUpdateDefaultCurrency;

      if (!awb || awb === "") {
        invalidRows.push({
          row: rowIndex + 2,
          awb: awb || "(empty)",
          error: "Missing AWB number",
        });
        return;
      }

      const parsedAmount = parseFloat(amount);
      if (isNaN(parsedAmount)) {
        invalidRows.push({
          row: rowIndex + 2,
          awb: String(awb),
          error: `Invalid amount: "${amount}"`,
        });
        return;
      }

      billingItems.push({
        awb: String(awb).trim(),
        amount: parsedAmount,
        currency: String(currency || bulkUpdateDefaultCurrency).trim().toUpperCase(),
      });
    });

    // Show validation errors if any
    if (invalidRows.length > 0 && billingItems.length === 0) {
      setBulkUpdateResults({
        totalProcessed: bulkUpdateFileData.length,
        successCount: 0,
        failedCount: invalidRows.length,
        errors: invalidRows,
      });
      setBulkUpdateStep("results");
      setError("All rows failed validation. Please check your file.");
      return;
    }

    // Proceed with valid items
    setBulkUpdateLoading(true);
    setBatchCreating(true);
    setBatchCreateProgress("Preparing batch task...");
    setBulkUpdateStep("processing");

    try {
      // Create batch task
      setBatchCreateProgress("Creating batch task...");
      const task = await createBatchTask(billingItems);
      setBatchTaskId(task.taskId);
      setBatchTaskProgress(task);

      // Start processing
      setBatchCreateProgress("Starting batch processing...");
      await startBatchProcessing(task.taskId);

      // Start polling for progress
      setBatchCreateProgress("Processing items...");
      setBatchCreating(false);
      startProgressPolling(task.taskId);

      // Store validation errors for results display
      if (invalidRows.length > 0) {
        setBulkUpdateResults({
          validationErrors: invalidRows,
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to create batch task");
      setBulkUpdateStep("mapping");
    } finally {
      setBulkUpdateLoading(false);
      setBatchCreating(false);
      setBatchCreateProgress("");
    }
  };

  // View batch task details from history
  const viewBatchTaskDetails = async (taskId) => {
    setBulkUpdateLoading(true);
    try {
      const details = await getBatchDetails(taskId);
      setBatchTaskId(taskId);
      setBatchTaskDetails(details);
      setBatchTaskProgress(details.task);
      setBulkUpdateStep("results");
    } catch (err) {
      setError("Failed to load task details");
    } finally {
      setBulkUpdateLoading(false);
    }
  };

  // Download batch error report
  const downloadBatchErrorReport = () => {
    if (!batchTaskDetails?.failedItems?.length) return;

    const errorData = batchTaskDetails.failedItems.map((item) => ({
      "AWB Number": item.awbNumber,
      "Amount": item.billedAmount,
      "Currency": item.billedCurrency,
      "Error Code": item.errorCode || "",
      "Error Message": item.errorMessage || "",
      "Retry Count": item.retryCount,
    }));

    const ws = XLSX.utils.json_to_sheet(errorData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Failed Items");

    XLSX.writeFile(wb, `batch_errors_${batchTaskId}.xlsx`);
  };

  // Download batch success report
  const downloadBatchSuccessReport = () => {
    if (!batchTaskDetails?.successItems?.length) return;

    const successData = batchTaskDetails.successItems.map((item) => ({
      "AWB Number": item.awbNumber,
      "Amount": item.billedAmount,
      "Currency": item.billedCurrency,
      "Status": item.status,
      "Processing Time (ms)": item.processingDurationMs || "",
    }));

    const ws = XLSX.utils.json_to_sheet(successData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Successful Items");

    XLSX.writeFile(wb, `batch_success_${batchTaskId}.xlsx`);
  };

  // ===================== END BATCH TASK FUNCTIONS =====================

  // ===================== END BULK UPDATE FUNCTIONS =====================

  // Fetch partners
  useEffect(() => {
    const fetchPartners = async () => {
      const baseApiUrl = getApiBaseUrl();
      try {
        const response = await axios.get(`${baseApiUrl}/v1.0/partner/retrieve_list?page=0`);
        setPartners(response.data.data || []);
      } catch (err) {
        console.error("Error fetching partners:", err);
      }
    };
    fetchPartners();
  }, []);

  // Toggle status selection
  const toggleStatus = (status) => {
    setSelectedStatuses((prev) => {
      if (prev.includes(status)) {
        // Don't allow deselecting all - keep at least one
        if (prev.length === 1) return prev;
        return prev.filter((s) => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  // Select all statuses
  const selectAllStatuses = () => {
    setSelectedStatuses(availableStatuses.map((s) => s.value));
  };

  // Clear all statuses (keep at least DELIVERED)
  const clearAllStatuses = () => {
    setSelectedStatuses(["DELIVERED"]);
  };

  // Helper function to split date range into weekly chunks
  const splitDateRange = (startDate, endDate, chunkDays = 7) => {
    const chunks = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    let currentStart = new Date(start);
    
    while (currentStart <= end) {
      const currentEnd = new Date(currentStart);
      currentEnd.setDate(currentEnd.getDate() + chunkDays - 1);
      
      // Don't exceed the end date
      if (currentEnd > end) {
        currentEnd.setTime(end.getTime());
      }
      
      chunks.push({
        from: currentStart.toISOString().split("T")[0],
        to: currentEnd.toISOString().split("T")[0],
      });
      
      // Move to next chunk
      currentStart = new Date(currentEnd);
      currentStart.setDate(currentStart.getDate() + 1);
    }
    
    return chunks;
  };

  // Fetch orders using consolidated API with progress tracking
  const fetchOrders = useCallback(async () => {
    if (!fromDate || !toDate) return;

    // Cancel any ongoing fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this fetch
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Increment fetch ID to track latest request
    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;

    setLoading(true);
    setError("");
    setOrders([]);
    setAllOrders([]);

    try {
    // Calculate date range in days
    const startDate = new Date(fromDate);
    const endDate = new Date(toDate);
    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

    // Split into weekly chunks
    const chunks = daysDiff <= 7 
      ? [{ from: fromDate, to: toDate }]
      : splitDateRange(fromDate, toDate, 7);
    
    const fetchStartTime = Date.now();
    
    setFetchProgress({
      isActive: true,
      isComplete: false,
      currentChunk: 0,
      totalChunks: chunks.length,
      successCount: 0,
      failedCount: 0,
      currentFromDate: "",
      currentToDate: "",
      completedRanges: [],
      failedRanges: [],
      totalRecords: 0,
      startTime: fetchStartTime,
      avgTimePerChunk: 0,
    });

    // Use Map for O(1) duplicate checking
    const ordersMap = new Map();
    let successCount = 0;
    let failedCount = 0;
    const completedRanges = [];
    const failedRanges = [];
    let totalTime = 0;

    for (let i = 0; i < chunks.length; i++) {
      // Check if this fetch was cancelled (new fetch started)
      if (currentFetchId !== fetchIdRef.current) {
        console.log("Fetch cancelled - newer request started");
        return;
      }

      const chunk = chunks[i];
      const chunkStartTime = Date.now();
      
      setFetchProgress(prev => ({
        ...prev,
        currentChunk: i + 1,
        currentFromDate: chunk.from,
        currentToDate: chunk.to,
      }));

      try {
        const response = await axios.get(
          `${API_URL}shypvdriverapp/jobs/getBillingOrders?fromDate=${chunk.from}&toDate=${chunk.to}&status=ALL`,
          { signal: abortController.signal }
        );

        // Check again after await - might have been cancelled
        if (currentFetchId !== fetchIdRef.current) {
          console.log("Fetch cancelled after response - newer request started");
          return;
        }

        const ordersData = response.data.data?.orders || [];
        
        // Use Map for O(1) insertion and duplicate prevention
        ordersData.forEach(order => {
          const k = order.orderId != null ? 'O' + order.orderId : order.awb;
          if (k != null && !ordersMap.has(k)) {
            ordersMap.set(k, order);
          }
        });
        
        successCount++;
        const chunkTime = Date.now() - chunkStartTime;
        totalTime += chunkTime;
        const avgTime = totalTime / successCount;
        
        completedRanges.push({ from: chunk.from, to: chunk.to, count: ordersData.length });

        const allFetchedOrders = Array.from(ordersMap.values());

        // Update orders progressively; record count = what actually shows (after filters)
        setAllOrders(allFetchedOrders);
        const visible = applyFiltersToOrders(allFetchedOrders);

        setFetchProgress(prev => ({
          ...prev,
          successCount,
          completedRanges: [...completedRanges],
          totalRecords: visible.length,
          avgTimePerChunk: avgTime,
        }));

      } catch (err) {
        // Ignore abort errors (expected when cancelled)
        if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
          console.log("Fetch aborted");
          return;
        }

        // Check if cancelled
        if (currentFetchId !== fetchIdRef.current) {
          return;
        }

        console.error(`Error fetching chunk ${chunk.from} to ${chunk.to}:`, err);
        failedCount++;
        failedRanges.push({ from: chunk.from, to: chunk.to, error: err.message });

        setFetchProgress(prev => ({
          ...prev,
          failedCount,
          failedRanges: [...failedRanges],
        }));
      }

      // Small delay between requests to prevent overwhelming the server
      if (i < chunks.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    // Final check before updating final state
    if (currentFetchId !== fetchIdRef.current) {
      return;
    }

    // Final update with consolidated data
    const finalOrders = Array.from(ordersMap.values());
    updateStatusOptions(finalOrders);

    // Show completion status
    if (failedCount > 0) {
      setError(`Completed with ${failedCount} failed request(s). Some data may be missing.`);
    }

    // Mark as complete
    setFetchProgress(prev => ({ ...prev, isActive: false, isComplete: true }));
    } finally {
      // Guarantee the spinner clears for the LATEST fetch on every exit path.
      // Previously, an early return or an unexpected throw could strand loading=true,
      // which hides already-fetched rows because <DataTable progressPending={loading} />.
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false);
        setFetchProgress(prev => ({ ...prev, isActive: false, isComplete: true }));
      }
    }
  }, [API_URL, fromDate, toDate]);

  // Auto fetch only once on initial page load (for last 7 days)
  useEffect(() => {
    if (didInitialFetchRef.current) return;
    if (!fromDate || !toDate) return;
    didInitialFetchRef.current = true;
    fetchOrders();
  }, [fromDate, toDate, fetchOrders]);

  // Apply filters to orders
  const applyFiltersToOrders = (ordersData) => {
    let filtered = [...ordersData];

    if (!includeAllOrders && selectedStatuses.length > 0 && !selectedStatuses.includes("ALL")) {
      filtered = filtered.filter((order) => selectedStatuses.includes(order.type));
    }

    if (billedFilter === "NOT_BILLED") {
      filtered = filtered.filter((order) => !order.isBilled);
    } else if (billedFilter === "BILLED") {
      filtered = filtered.filter((order) => order.isBilled);
    }

    if (selectedPartner !== "0") {
      filtered = filtered.filter((order) =>
        order.awb?.includes(`UB${String(selectedPartner).padStart(2, "0")}-`)
      );
    }

    setOrders(filtered);
    return filtered;
  };

  // Update status options
  const updateStatusOptions = (ordersData) => {
    const statusOptions = ALL_STATUSES.map((status) => ({
      value: status,
      label: status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      color: getStatusColor(status),
      count: ordersData.filter((o) => o.type === status).length,
    }));
    setAvailableStatuses(statusOptions);
  };

  // Apply local filters when billedFilter, partner or statuses change
  useEffect(() => {
    if (allOrders.length === 0) return;
    applyFiltersToOrders(allOrders);
  }, [allOrders, billedFilter, selectedPartner, selectedStatuses, includeAllOrders]);

  // Handle row selection
  const handleRowSelected = (state) => {
    setSelectedRows(state.selectedRows);
  };

  // Add selected orders to billing list (only AWB with empty amount)
  const addToBillingList = () => {
    if (selectedRows.length === 0) {
      setError("Please select at least one order");
      return;
    }

    const newItems = selectedRows
      .filter((row) => !billingList.some((item) => item.awb === row.awb))
      .map((row) => ({
        awb: row.awb,
        amount: "",
      }));

    if (newItems.length === 0) {
      setError("All selected orders are already in the billing list");
      return;
    }

    setBillingList([...billingList, ...newItems]);
    setSuccess(`Added ${newItems.length} AWB(s) to billing list`);
    setClearSelectedRows(!clearSelectedRows);
    setSelectedRows([]);
  };

  // Update amount for a billing list item - optimized with functional update
  const updateBillingItemAmount = useCallback((awb, amount) => {
    setBillingList((prev) =>
      prev.map((item) =>
        item.awb === awb ? { ...item, amount: amount } : item
      )
    );
  }, []);

  // Remove from billing list - optimized with functional update
  const removeFromBillingList = useCallback((awb) => {
    setBillingList((prev) => prev.filter((item) => item.awb !== awb));
  }, []);

  // Clear billing list
  const clearBillingList = () => {
    setBillingList([]);
    setSuccess("Billing list cleared");
  };

  // Submit billing update using Batch Task API
  const submitBillingUpdate = async () => {
    if (billingList.length === 0) {
      setError("Billing list is empty");
      return;
    }
    if (!billedDate) {
      setError("Please select a billed date");
      return;
    }

    setLoading(true);
    setBatchCreating(true);
    setBatchCreateProgress("Preparing billing batch...");
    
    // Build items with AWB, amount, currency, and billedDate for batch API
    const billingItems = billingList.map((item) => ({
      awb: item.awb,
      amount: parseFloat(item.amount) || 0,
      currency: billingCurrency,
      billedDate: billedDate,
    }));

    try {
      // Create batch task
      setBatchCreateProgress("Creating batch task...");
      const task = await createBatchTask(billingItems);
      console.log("Batch task created:", task);
      
      setBatchTaskId(task.taskId);
      
      // Start batch processing
      setBatchCreateProgress("Starting batch processing...");
      await startBatchProcessing(task.taskId);
      console.log("Batch processing started");
      
      // Start polling for progress
      setBatchCreateProgress("Processing items...");
      setBatchCreating(false);
      startProgressPolling(task.taskId);
      
      // Show processing modal
      setBulkUpdateStep("processing");
      setShowBulkUpdateModal(true);
      
      setSuccess(`✓ Batch task created! Processing ${billingList.length} orders...`);
      setBillingList([]);
      setBilledDate("");
      
    } catch (err) {
      console.error("Error creating batch task:", err);
      console.error("Error details:", err.response?.data);
      setError(err.response?.data?.message || "Failed to create billing batch task. Please try again.");
    } finally {
      setLoading(false);
      setBatchCreating(false);
      setBatchCreateProgress("");
    }
  };

  // All available columns configuration
  const allColumnsConfig = [
    {
      id: "awb",
      name: "AWB",
      selector: (row) => row.awb || "",
      sortable: true,
      cell: (row) => (
        <span style={{ color: "#3b82f6", fontWeight: "600", fontSize: "13px" }}>
          {row.awb || ""}
        </span>
      ),
      minWidth: "150px",
      wrap: true,
    },
    {
      id: "customerName",
      name: "Customer Name",
      selector: (row) => row.consignee?.name || "",
      sortable: true,
      minWidth: "140px",
      wrap: true,
    },
    {
      id: "clientRef",
      name: "Client Ref",
      selector: (row) => row.businessRefNo || "",
      sortable: true,
      cell: (row) => (
        <div style={{ wordWrap: "break-word", whiteSpace: "normal", textAlign: "left" }}>
          {row.businessRefNo || ""}
        </div>
      ),
      minWidth: "150px",
      wrap: true,
    },
    {
      id: "phone",
      name: "Phone",
      selector: (row) => row.consignee?.phone || "",
      sortable: true,
      minWidth: "130px",
      wrap: true,
    },
    {
      id: "address",
      name: "Address",
      selector: (row) => row.consignee?.address || "",
      sortable: true,
      cell: (row) => (
        <div style={{ wordWrap: "break-word", whiteSpace: "normal", textAlign: "left" }}>
          {row.consignee?.address || ""}
        </div>
      ),
      minWidth: "200px",
      wrap: true,
    },
    {
      id: "city",
      name: "City",
      selector: (row) => row.city || "",
      sortable: true,
      minWidth: "100px",
      wrap: true,
    },
    {
      id: "shipper",
      name: "Shipper",
      selector: (row) => row.shipper?.name || "",
      sortable: true,
      minWidth: "130px",
      wrap: true,
    },
    {
      id: "shipperPhone",
      name: "Shipper Phone",
      selector: (row) => row.shipper?.phone || "",
      sortable: true,
      minWidth: "130px",
      wrap: true,
    },
    {
      id: "originCity",
      name: "Origin City",
      selector: (row) => row.origin || row.shipper?.city || "",
      sortable: true,
      minWidth: "120px",
      wrap: true,
    },
    {
      id: "destinationCity",
      name: "Destination City",
      selector: (row) => row.destination || row.city || "",
      sortable: true,
      minWidth: "130px",
      wrap: true,
    },
    {
      id: "driver",
      name: "Driver",
      selector: (row) => row.driverName || "",
      sortable: true,
      cell: (row) => (
        <span style={{ fontSize: "13px" }}>
          {row.driverName || ""}
        </span>
      ),
      minWidth: "150px",
      wrap: true,
    },
    {
      id: "driverTeam",
      name: "Driver Team",
      selector: (row) => row.driverTeam || "",
      sortable: true,
      cell: (row) => (
        <span style={{ fontSize: "13px" }}>
          {row.driverTeam || ""}
        </span>
      ),
      minWidth: "120px",
      wrap: true,
    },
    {
      id: "driverZone",
      name: "Driver Zone",
      selector: (row) => row.driverZone || "",
      sortable: true,
      cell: (row) => (
        <span style={{ fontSize: "13px" }}>
          {row.driverZone || ""}
        </span>
      ),
      minWidth: "150px",
      wrap: true,
    },
    {
      id: "status",
      name: "Status",
      selector: (row) => row.type || "",
      sortable: true,
      cell: (row) => (
        <span
          style={{
            padding: "4px 8px",
            borderRadius: "6px",
            fontSize: "11px",
            fontWeight: "500",
            backgroundColor: getStatusColor(row.type) + "20",
            color: getStatusColor(row.type),
            whiteSpace: "nowrap",
          }}
        >
          {row.type || ""}
        </span>
      ),
      minWidth: "140px",
    },
    {
      id: "type",
      name: "Type",
      selector: (row) => row.orderType || "",
      sortable: true,
      minWidth: "100px",
      wrap: true,
    },
    {
      id: "assignedDate",
      name: "Assigned Date",
      selector: (row) => row.date || "",
      sortable: true,
      minWidth: "120px",
      wrap: true,
    },
    {
      id: "weight",
      name: "Weight",
      selector: (row) => row.weight || "",
      sortable: true,
      minWidth: "90px",
      wrap: true,
    },
    {
      id: "volWeight",
      name: "Vol Weight",
      selector: (row) => row.volWeight || row.volumetricWeight || "",
      sortable: true,
      minWidth: "100px",
      wrap: true,
    },
    {
      id: "totalCod",
      name: "COD Amount",
      selector: (row) => (row.totalCod != null && row.totalCod !== "" ? row.totalCod : ""),
      sortable: true,
      minWidth: "110px",
      wrap: true,
    },
    {
      id: "codReceived",
      name: "COD Received",
      selector: (row) => (row.codReceived != null && row.codReceived !== "" ? row.codReceived : ""),
      sortable: true,
      minWidth: "120px",
      wrap: true,
    },
    {
      id: "billedDate",
      name: "Billed Date",
      selector: (row) => row.billedDate || "",
      sortable: true,
      minWidth: "120px",
      wrap: true,
    },
    {
      id: "noOfItems",
      name: "No Of Items/PC",
      selector: (row) => row.noOfItems || row.pieces || "",
      sortable: true,
      minWidth: "110px",
      wrap: true,
    },
    {
      id: "orderCreationDate",
      name: "Order Creation Date",
      selector: (row) => row.orderCreationDate || row.createdAt || "",
      sortable: true,
      minWidth: "150px",
      wrap: true,
    },
    {
      id: "lastTransDate",
      name: "Last Trans Date",
      selector: (row) => row.date || row.lastTransDate || "",
      sortable: true,
      minWidth: "130px",
      wrap: true,
    },
    {
      id: "billed",
      name: "Billed",
      selector: (row) => row.isBilled,
      sortable: true,
      cell: (row) => (
        <span
          style={{
            padding: "4px 10px",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            backgroundColor: row.isBilled ? "#d1fae5" : "#fef3c7",
            color: row.isBilled ? "#065f46" : "#92400e",
          }}
        >
          {row.isBilled ? "Yes" : "No"}
        </span>
      ),
      minWidth: "90px",
    },
    {
      id: "billedAmount",
      name: "Billed Amount",
      selector: (row) => row.billedAmount || "",
      sortable: true,
      cell: (row) => (
        <span style={{ fontSize: "13px", fontWeight: row.billedAmount ? "600" : "400", color: row.billedAmount ? "#059669" : "#9ca3af" }}>
          {row.billedAmount ? parseFloat(row.billedAmount).toFixed(2) : "-"}
        </span>
      ),
      minWidth: "120px",
    },
    {
      id: "billedCurrency",
      name: "Currency",
      selector: (row) => row.billedCurrency || "",
      sortable: true,
      cell: (row) => (
        <span style={{ fontSize: "13px", color: row.billedCurrency ? "#374151" : "#9ca3af" }}>
          {row.billedCurrency || "-"}
        </span>
      ),
      minWidth: "90px",
    },
    {
      id: "billedBy",
      name: "Billed By",
      selector: (row) => row.billedBy || "",
      sortable: true,
      cell: (row) => (
        <span style={{ fontSize: "12px", color: row.billedBy ? "#374151" : "#9ca3af" }}>
          {row.billedBy || "-"}
        </span>
      ),
      minWidth: "120px",
      wrap: true,
    },
    {
      id: "project",
      name: "Project",
      selector: (row) => row.projectName || "",
      sortable: true,
      cell: (row) => (
        <span style={{ fontSize: "12px", color: row.projectName ? "#1e40af" : "#9ca3af" }}>
          {row.projectName || "-"}
        </span>
      ),
      minWidth: "140px",
      wrap: true,
    },
    {
      id: "serviceTime",
      name: "Service Time",
      selector: (row) => row.serviceTime || "",
      sortable: true,
      cell: (row) => (
        <span style={{ fontSize: "12px", color: row.serviceTime === "Same Day" ? "#047857" : row.serviceTime === "Next Day" ? "#b45309" : "#9ca3af" }}>
          {row.serviceTime || "-"}
        </span>
      ),
      minWidth: "120px",
      wrap: true,
    },
  ];

  // Filter columns based on visibility - memoized for performance
  const columns = useMemo(() => 
    allColumnsConfig.filter((col) => visibleColumns.includes(col.id)),
    [visibleColumns]
  );

  // Toggle column visibility
  const toggleColumnVisibility = (columnId) => {
    setVisibleColumns((prev) => {
      if (prev.includes(columnId)) {
        // Don't allow hiding all columns - keep at least one
        if (prev.length === 1) return prev;
        return prev.filter((id) => id !== columnId);
      } else {
        return [...prev, columnId];
      }
    });
  };

  // Select all columns
  const selectAllColumns = () => {
    setVisibleColumns(allColumnsConfig.map((col) => col.id));
  };

  // Reset to default columns
  const resetColumns = () => {
    setVisibleColumns(["awb", "customerName", "clientRef", "phone", "address", "city", "shipper", "shipperPhone"]);
  };

  // Memoized filtered data for table search - handles millions of records efficiently
  const displayedOrders = useMemo(() => {
    if (!debouncedSearchText || debouncedSearchText.trim() === "") {
      return orders;
    }
    const searchLower = debouncedSearchText.toLowerCase().trim();
    return orders.filter((order) => {
      return (
        order.awb?.toLowerCase().includes(searchLower) ||
        order.consignee?.name?.toLowerCase().includes(searchLower) ||
        order.consignee?.phone?.toLowerCase().includes(searchLower) ||
        order.consignee?.address?.toLowerCase().includes(searchLower) ||
        order.businessRefNo?.toLowerCase().includes(searchLower) ||
        order.city?.toLowerCase().includes(searchLower) ||
        order.shipper?.name?.toLowerCase().includes(searchLower) ||
        order.driverName?.toLowerCase().includes(searchLower) ||
        order.type?.toLowerCase().includes(searchLower)
      );
    });
  }, [orders, debouncedSearchText]);

  // Column ID to export field mapping
  const columnExportMapping = {
    awb: { header: "AWB", getValue: (order) => order.awb ?? "" },
    customerName: { header: "Customer Name", getValue: (order) => order.consignee?.name ?? "" },
    clientRef: { header: "Client Ref", getValue: (order) => order.businessRefNo ?? "" },
    phone: { header: "Phone", getValue: (order) => order.consignee?.phone ?? "" },
    address: { header: "Address", getValue: (order) => order.consignee?.address ?? "" },
    city: { header: "City", getValue: (order) => order.city ?? "" },
    shipper: { header: "Shipper", getValue: (order) => order.shipper?.name ?? "" },
    shipperPhone: { header: "Shipper Phone", getValue: (order) => order.shipper?.phone ?? "" },
    originCity: { header: "Origin City", getValue: (order) => order.origin || order.shipper?.city || "" },
    destinationCity: { header: "Destination City", getValue: (order) => order.destination || order.city || "" },
    driver: { header: "Driver", getValue: (order) => order.driverName ?? "" },
    driverTeam: { header: "Driver Team", getValue: (order) => order.driverTeam ?? "" },
    driverZone: { header: "Driver Zone", getValue: (order) => order.driverZone ?? "" },
    status: { header: "Status", getValue: (order) => order.type ?? "" },
    type: { header: "Type", getValue: (order) => order.orderType ?? "" },
    assignedDate: { header: "Assigned Date", getValue: (order) => order.date ?? "" },
    weight: { header: "Weight", getValue: (order) => order.weight ?? "" },
    volWeight: { header: "Vol Weight", getValue: (order) => order.volWeight || order.volumetricWeight || "" },
    totalCod: { header: "COD Amount", getValue: (order) => (order.totalCod != null && order.totalCod !== "" ? order.totalCod : "") },
    codReceived: { header: "COD Received", getValue: (order) => (order.codReceived != null && order.codReceived !== "" ? order.codReceived : "") },
    billedDate: { header: "Billed Date", getValue: (order) => order.billedDate || "" },
    noOfItems: { header: "No Of Items/PC", getValue: (order) => order.noOfItems || order.pieces || "" },
    orderCreationDate: { header: "Order Creation Date", getValue: (order) => order.orderCreationDate || order.createdAt || "" },
    lastTransDate: { header: "Last Trans Date", getValue: (order) => order.date || order.lastTransDate || "" },
    billed: { header: "Billed", getValue: (order) => order.isBilled ? "Yes" : "No" },
    billedAmount: { header: "Billed Amount", getValue: (order) => order.billedAmount ? parseFloat(order.billedAmount).toFixed(2) : "" },
    billedCurrency: { header: "Currency", getValue: (order) => order.billedCurrency || "" },
    billedBy: { header: "Billed By", getValue: (order) => order.billedBy || "" },
    project: { header: "Project", getValue: (order) => order.projectName || "" },
    serviceTime: { header: "Service Time", getValue: (order) => order.serviceTime || "" },
  };

  // Export data - only export visible columns
  const flattenOrderData = (ordersToExport) => {
    const data = ordersToExport.map((order, index) => {
      const row = { "#": index + 1 };
      visibleColumns.forEach((colId) => {
        const mapping = columnExportMapping[colId];
        if (mapping) {
          row[mapping.header] = mapping.getValue(order);
        }
      });
      return row;
    });
    return data;
  };

  // Calculate stats
  const totalCOD = orders.reduce((sum, o) => sum + (parseFloat(o.totalCod) || 0), 0);
  const billingListCOD = billingList.reduce((sum, o) => sum + (parseFloat(o.totalCod) || 0), 0);

  return (
    <div style={styles.container}>
      {/* Alerts */}
      {error && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            padding: "16px 24px",
            borderRadius: "12px",
            backgroundColor: "#fef2f2",
            color: "#dc2626",
            border: "1px solid #fecaca",
            boxShadow: "0 10px 40px rgba(220, 38, 38, 0.2)",
            fontSize: "14px",
            fontWeight: "500",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            maxWidth: "600px",
            animation: "slideDownAlert 0.4s ease-out",
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          {error}
          <button
            onClick={() => setError("")}
            style={{ marginLeft: "auto", padding: "4px", backgroundColor: "transparent", border: "none", cursor: "pointer", color: "#dc2626" }}
          >
            ✕
          </button>
        </div>
      )}
      {success && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            padding: "16px 24px",
            borderRadius: "12px",
            backgroundColor: "#f0fdf4",
            color: "#16a34a",
            border: "1px solid #bbf7d0",
            boxShadow: "0 10px 40px rgba(22, 163, 74, 0.2)",
            fontSize: "14px",
            fontWeight: "500",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            maxWidth: "600px",
            animation: "slideDownAlert 0.4s ease-out",
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          {success}
          <button
            onClick={() => setSuccess("")}
            style={{ marginLeft: "auto", padding: "4px", backgroundColor: "transparent", border: "none", cursor: "pointer", color: "#16a34a" }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: "10px", verticalAlign: "middle" }}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
          Billing Orders
        </h1>
      </div>

      {/* Toggle View */}
      <div style={styles.toggleContainer}>
        <button
          style={{
            ...styles.toggleBtn,
            ...(mainView === "orders" ? styles.toggleBtnActive : styles.toggleBtnInactive),
          }}
          onClick={() => setMainView("orders")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <line x1="3" y1="9" x2="21" y2="9" />
            <line x1="9" y1="21" x2="9" y2="9" />
          </svg>
          Browse Orders
        </button>
        <button
          style={{
            ...styles.toggleBtn,
            ...(mainView === "billing" ? styles.toggleBtnActive : styles.toggleBtnInactive),
          }}
          onClick={() => setMainView("billing")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
            <rect x="9" y="3" width="6" height="4" rx="2" />
            <path d="M9 12h6" />
            <path d="M9 16h6" />
          </svg>
          Billing List ({billingList.length})
        </button>
        <button
          style={{
            ...styles.toggleBtn,
            ...(mainView === "history" ? styles.toggleBtnActive : styles.toggleBtnInactive),
          }}
          onClick={() => {
            if (mainView !== "history") {
              fetchBatchHistory();
            }
            setMainView("history");
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          Billing History
        </button>
      </div>

      {/* Stats Cards */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: "#3b82f6" }}>{orders.length}</div>
          <div style={styles.statLabel}>Orders Found</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: "#10b981" }}>
            {totalCOD.toFixed(2)}
          </div>
          <div style={styles.statLabel}>Total COD (AED)</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: "#f59e0b" }}>{billingList.length}</div>
          <div style={styles.statLabel}>In Billing List</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: "#8b5cf6" }}>
            {billingListCOD.toFixed(2)}
          </div>
          <div style={styles.statLabel}>Billing List COD</div>
        </div>
      </div>

      {mainView === "orders" ? (
        <>
          {/* Filters Card */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: "8px", verticalAlign: "middle" }}>
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                Filters
              </h3>
            </div>
            <div style={styles.cardBody}>
              {/* Info Banner - Show selected filters INSIDE card */}
              <div style={{ ...styles.infoBanner, marginBottom: "20px" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                <span><strong>Date Range:</strong> {fromDate} to {toDate}</span>
                <span>•</span>
                <span><strong>Status:</strong> {includeAllOrders ? "All Statuses" : selectedStatuses.join(", ")}</span>
                <span>•</span>
                <span><strong>Billing:</strong> {billedFilter === "ALL" ? "All" : billedFilter === "BILLED" ? "Billed Only" : "Not Billed"}</span>
                <span>•</span>
                <span><strong>Partner:</strong> {selectedPartner === "0" ? "All Partners" : partners.find(p => String(p.pid) === selectedPartner)?.pname || selectedPartner}</span>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>From Date</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>To Date</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Billing Status</label>
                  <select
                    value={billedFilter}
                    onChange={(e) => setBilledFilter(e.target.value)}
                    style={styles.select}
                  >
                    <option value="ALL">All</option>
                    <option value="NOT_BILLED">Not Billed</option>
                    <option value="BILLED">Billed</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Partner</label>
                  <select
                    value={selectedPartner}
                    onChange={(e) => setSelectedPartner(e.target.value)}
                    style={styles.select}
                  >
                    <option value="0">All Partners</option>
                    {partners
                      .sort((a, b) => a.pname?.localeCompare(b.pname))
                      .map((partner) => (
                        <option key={partner.pid} value={partner.pid}>
                          {String(partner.pid).padStart(2, "0")} - {partner.pname}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              {/* Include All Orders Checkbox */}
              <div style={{ marginBottom: "16px", padding: "12px 16px", backgroundColor: "#fefce8", borderRadius: "8px", border: "1px solid #fde047" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", fontSize: "14px", fontWeight: "500", color: "#854d0e" }}>
                  <input
                    type="checkbox"
                    checked={includeAllOrders}
                    onChange={(e) => setIncludeAllOrders(e.target.checked)}
                    style={styles.checkbox}
                  />
                  <span>✓ Include ALL Orders (ignore status filter)</span>
                </label>
              </div>

              {/* Dynamic Status Selection - from actual data */}
              <div style={{ marginBottom: "8px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
                  <label style={styles.label}>Order Status (select multiple) - {availableStatuses.length} statuses found</label>
                  <div style={{ display: "flex", gap: "12px" }}>
                    <button
                      onClick={selectAllStatuses}
                      style={{ fontSize: "12px", color: "#3b82f6", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
                      disabled={includeAllOrders}
                    >
                      Select All
                    </button>
                    <button
                      onClick={clearAllStatuses}
                      style={{ fontSize: "12px", color: "#ef4444", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
                      disabled={includeAllOrders}
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div style={{ ...styles.statusCheckboxGrid, opacity: includeAllOrders ? 0.5 : 1 }}>
                  {availableStatuses.length === 0 ? (
                    <span style={{ color: "#6b7280", fontSize: "13px" }}>Loading statuses... (fetch data first)</span>
                  ) : (
                    availableStatuses.map((status) => (
                      <label
                        key={status.value}
                        style={{
                          ...styles.checkboxLabel,
                          ...(selectedStatuses.includes(status.value) && !includeAllOrders ? styles.checkboxLabelActive : {}),
                          cursor: includeAllOrders ? "not-allowed" : "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedStatuses.includes(status.value)}
                          onChange={() => toggleStatus(status.value)}
                          disabled={includeAllOrders}
                          style={styles.checkbox}
                        />
                        <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: status.color }} />
                        {status.label} ({status.count})
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Fetch Orders Button - Bottom Right */}
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
                <button
                  onClick={fetchOrders}
                  disabled={loading || !fromDate || !toDate}
                  style={{
                    ...styles.btnPrimary,
                    padding: "12px 24px",
                    fontSize: "14px",
                    fontWeight: "600",
                    opacity: loading || !fromDate || !toDate ? 0.6 : 1,
                    cursor: loading || !fromDate || !toDate ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? (
                    <>
                      <div style={{
                        width: "16px",
                        height: "16px",
                        border: "2px solid #ffffff",
                        borderTop: "2px solid transparent",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }} />
                      Fetching...
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Fetch Orders
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Action Bar */}
          {selectedRows.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                padding: "16px 20px",
                backgroundColor: "#eff6ff",
                borderRadius: "12px",
                marginBottom: "20px",
                border: "1px solid #bfdbfe",
              }}
            >
              <span style={{ fontWeight: "600", color: "#1e40af" }}>
                {selectedRows.length} order(s) selected
              </span>
              <button style={styles.btnSuccess} onClick={addToBillingList}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add to Billing List
              </button>
            </div>
          )}

          {/* Progress Bar for Split Fetch */}
          {(fetchProgress.isActive || (fetchProgress.isComplete && fetchProgress.failedRanges.length > 0)) && (
            <div
              style={{
                ...styles.card,
                marginBottom: "16px",
                padding: "16px 20px",
                backgroundColor: "#ffffff",
                border: "1px solid #e5e7eb",
              }}
            >
              {/* Minimal status row: only records captured + approx time */}
              <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "12px" }}>
                {fetchProgress.isActive && (
                  <div
                    style={{
                      width: "18px",
                      height: "18px",
                      border: "2px solid #e5e7eb",
                      borderTop: "2px solid #3b82f6",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                      flexShrink: 0,
                    }}
                  />
                )}
                <span style={{ fontSize: "14px", fontWeight: "700", color: "#111827" }}>
                  {fetchProgress.isActive ? "Loading orders…" : "Done"}
                </span>
                <span style={{ fontSize: "13px", color: "#111827" }}>
                  <strong>{fetchProgress.totalRecords.toLocaleString()}</strong> records captured
                </span>
                {fetchProgress.isActive && fetchProgress.avgTimePerChunk > 0 && (
                  <span style={{ fontSize: "13px", color: "#6b7280" }}>
                    approx <strong style={{ color: "#6366f1" }}>{Math.max(1, Math.ceil(((fetchProgress.totalChunks - fetchProgress.successCount) * fetchProgress.avgTimePerChunk) / 1000))}s</strong> remaining
                  </span>
                )}
              </div>

              {/* Progress Bar */}
              <div
                style={{
                  width: "100%",
                  height: "8px",
                  backgroundColor: "#e5e7eb",
                  borderRadius: "4px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${fetchProgress.totalChunks > 0 ? (fetchProgress.successCount / fetchProgress.totalChunks) * 100 : 0}%`,
                    height: "100%",
                    backgroundColor: "#10b981",
                    borderRadius: "4px",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>

              {/* (detailed API/parts stats hidden) */}
              <div style={{ display: "none" }}>
                <div>
                  {fetchProgress.currentChunk}/{fetchProgress.totalChunks}
                  {fetchProgress.successCount}
                  {fetchProgress.failedCount}
                </div>
                {fetchProgress.isActive && fetchProgress.currentFromDate && (
                  <div>
                    {fetchProgress.currentFromDate} → {fetchProgress.currentToDate}
                  </div>
                )}
              </div>

              {/* Failed Ranges with Retry */}
              {fetchProgress.failedRanges.length > 0 && (
                <div
                  style={{
                    marginTop: "12px",
                    padding: "12px",
                    backgroundColor: "#fef2f2",
                    borderRadius: "6px",
                    border: "1px solid #fecaca",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: "0 0 6px", fontSize: "13px", fontWeight: "500", color: "#dc2626" }}>
                        Failed to fetch {fetchProgress.failedCount} date range(s):
                      </p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                        {fetchProgress.failedRanges.map((range, idx) => (
                          <span
                            key={idx}
                            style={{
                              padding: "2px 8px",
                              backgroundColor: "#fee2e2",
                              borderRadius: "4px",
                              fontSize: "12px",
                              color: "#991b1b",
                            }}
                          >
                            {range.from} → {range.to}
                          </span>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={retryFailedChunks}
                      disabled={fetchProgress.isActive}
                      style={{
                        padding: "6px 12px",
                        backgroundColor: fetchProgress.isActive ? "#e5e7eb" : "#ef4444",
                        color: fetchProgress.isActive ? "#9ca3af" : "#ffffff",
                        border: "none",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: "500",
                        cursor: fetchProgress.isActive ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M23 4v6h-6" />
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                      </svg>
                      Retry All
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Orders Table */}
          <div style={{ ...styles.card, overflow: "hidden" }}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>Orders</h3>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <span style={{ fontSize: "13px", color: "#6b7280" }}>
                  {loading ? "Loading..." : `Showing ${orders.length} orders`} • {fromDate} to {toDate}
                </span>
                <button
                  type="button"
                  onClick={downloadExcel}
                  disabled={loading || orders.length === 0}
                  style={{
                    ...styles.btnSuccess,
                    textDecoration: "none",
                    padding: "8px 14px",
                    fontSize: "13px",
                    opacity: loading || orders.length === 0 ? 0.6 : 1,
                    cursor: loading || orders.length === 0 ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download Excel ({orders.length})
                </button>
                <div style={{ position: "relative" }}>
                  <button
                    style={{
                      ...styles.btnSecondary,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "8px 14px",
                      fontSize: "13px",
                    }}
                    onClick={() => setShowColumnSelector(!showColumnSelector)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="7" height="7" />
                      <rect x="14" y="3" width="7" height="7" />
                      <rect x="14" y="14" width="7" height="7" />
                      <rect x="3" y="14" width="7" height="7" />
                    </svg>
                    Columns ({visibleColumns.length}/{allColumnsConfig.length})
                  </button>
                  {showColumnSelector && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        right: 0,
                        marginTop: "8px",
                        backgroundColor: "#fff",
                        borderRadius: "12px",
                        boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
                        border: "1px solid #e2e8f0",
                        padding: "16px",
                        zIndex: 100,
                        minWidth: "280px",
                        maxHeight: "400px",
                        overflowY: "auto",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", paddingBottom: "12px", borderBottom: "1px solid #e2e8f0" }}>
                        <span style={{ fontWeight: "600", fontSize: "14px", color: "#374151" }}>Select Columns</span>
                        <button
                          onClick={() => setShowColumnSelector(false)}
                          style={{ background: "none", border: "none", cursor: "pointer", padding: "4px" }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </div>
                      <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                        <button
                          onClick={selectAllColumns}
                          style={{
                            padding: "6px 12px",
                            fontSize: "12px",
                            backgroundColor: "#eff6ff",
                            color: "#3b82f6",
                            border: "1px solid #bfdbfe",
                            borderRadius: "6px",
                            cursor: "pointer",
                          }}
                        >
                          Select All
                        </button>
                        <button
                          onClick={resetColumns}
                          style={{
                            padding: "6px 12px",
                            fontSize: "12px",
                            backgroundColor: "#f3f4f6",
                            color: "#6b7280",
                            border: "1px solid #e2e8f0",
                            borderRadius: "6px",
                            cursor: "pointer",
                          }}
                        >
                          Reset
                        </button>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        {allColumnsConfig.map((col) => (
                          <label
                            key={col.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              padding: "8px 10px",
                              backgroundColor: visibleColumns.includes(col.id) ? "#eff6ff" : "#f8fafc",
                              borderRadius: "6px",
                              cursor: "pointer",
                              border: visibleColumns.includes(col.id) ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
                              fontSize: "13px",
                              fontWeight: "500",
                              color: visibleColumns.includes(col.id) ? "#1e40af" : "#4b5563",
                              transition: "all 0.15s",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={visibleColumns.includes(col.id)}
                              onChange={() => toggleColumnVisibility(col.id)}
                              style={{ width: "16px", height: "16px", accentColor: "#3b82f6" }}
                            />
                            {col.name}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Custom Search Input - replaces DataTableExtensions for better performance */}
            <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ position: "relative", flex: "0 0 300px" }}>
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={tableSearchText}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px 10px 36px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: "14px",
                    outline: "none",
                    transition: "border-color 0.2s",
                  }}
                />
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9ca3af"
                  strokeWidth="2"
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
              </div>
              <span style={{ fontSize: "13px", color: "#6b7280" }}>
                Showing {displayedOrders.length.toLocaleString()} of {orders.length.toLocaleString()} orders
              </span>
            </div>
            <div style={{ width: "100%", overflowX: "auto", position: "relative" }}>
              <DataTable
                columns={columns}
                data={displayedOrders}
                keyField="awb"
                pagination
                paginationPerPage={50}
                paginationRowsPerPageOptions={[25, 50, 100, 200, 500]}
                paginationComponentOptions={{
                  rowsPerPageText: "Rows per page:",
                  rangeSeparatorText: "of",
                  noRowsPerPage: false,
                  selectAllRowsItem: false,
                }}
                highlightOnHover
                dense
                selectableRows
                onSelectedRowsChange={handleRowSelected}
                clearSelectedRows={clearSelectedRows}
                defaultSortFieldId={1}
                defaultSortAsc={false}
                responsive
                progressPending={loading}
                progressComponent={
                  <div style={{ padding: "40px", textAlign: "center" }}>
                    <div style={{
                      width: "40px",
                      height: "40px",
                      margin: "0 auto 12px",
                      border: "4px solid #e5e7eb",
                      borderTop: "4px solid #3b82f6",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }} />
                    <p style={{ color: "#6b7280", fontSize: "14px" }}>Loading orders...</p>
                  </div>
                }
                noDataComponent={
                  <div style={{ padding: "40px", textAlign: "center", color: "#6b7280" }}>
                    No orders found. Try adjusting your filters or search.
                  </div>
                }
                customStyles={{
                  table: {
                    style: {
                      width: "100%",
                      tableLayout: "auto",
                    },
                  },
                  headRow: {
                    style: {
                      backgroundColor: "#f9fafb",
                      borderBottom: "2px solid #e5e7eb",
                      minHeight: "48px",
                    },
                  },
                  headCells: {
                    style: {
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#374151",
                      paddingLeft: "12px",
                      paddingRight: "12px",
                      whiteSpace: "nowrap",
                      overflow: "visible",
                      textOverflow: "unset",
                    },
                  },
                  rows: {
                    style: {
                      minHeight: "48px",
                    },
                    highlightOnHoverStyle: {
                      backgroundColor: "#f3f4f6",
                    },
                  },
                  cells: {
                    style: {
                      fontSize: "13px",
                      color: "#4b5563",
                      paddingLeft: "12px",
                      paddingRight: "12px",
                      overflow: "visible",
                    },
                  },
                }}
              />
            </div>
          </div>
        </>
      ) : mainView === "billing" ? (
        /* Billing List View */
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: "8px", verticalAlign: "middle" }}>
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                <rect x="9" y="3" width="6" height="4" rx="2" />
              </svg>
              Billing List ({billingList.length} orders)
            </h3>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                style={{
                  padding: "8px 14px",
                  backgroundColor: "#8b5cf6",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "13px",
                }}
                onClick={() => setShowBulkUpdateModal(true)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                Bulk Upload
              </button>
              {billingList.length > 0 && (
                <button style={styles.btnDanger} onClick={clearBillingList}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  Clear All
                </button>
              )}
            </div>
          </div>
          <div style={styles.cardBody}>
            {billingList.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "#6b7280" }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ margin: "0 auto 16px", opacity: 0.5 }}>
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                  <rect x="9" y="3" width="6" height="4" rx="2" />
                </svg>
                <p style={{ fontSize: "16px", marginBottom: "8px" }}>Billing list is empty</p>
                <p style={{ fontSize: "14px" }}>Select orders from the "Browse Orders" tab or use Bulk Upload</p>
              </div>
            ) : (
              <>
                {/* Currency & Date Selection */}
                <div style={{ marginBottom: "20px", display: "flex", alignItems: "flex-end", gap: "16px", flexWrap: "wrap" }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Currency</label>
                    <select
                      value={billingCurrency}
                      onChange={(e) => setBillingCurrency(e.target.value)}
                      style={{ ...styles.input, width: "120px" }}
                    >
                      <option value="AED">AED</option>
                      <option value="SAR">SAR</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Billed Date *</label>
                    <input
                      type="date"
                      value={billedDate}
                      onChange={(e) => setBilledDate(e.target.value)}
                      style={{ ...styles.input, width: "180px" }}
                    />
                  </div>
                  <button
                    style={{
                      ...styles.btnSuccess,
                      opacity: !billedDate ? 0.6 : 1,
                      cursor: !billedDate ? "not-allowed" : "pointer",
                    }}
                    onClick={submitBillingUpdate}
                    disabled={loading || !billedDate}
                  >
                    {loading ? (
                      <span style={{ display: "inline-block", width: "16px", height: "16px", border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    )}
                    {loading ? "Processing..." : `Submit ${billingList.length} Items`}
                  </button>
                </div>

                {/* Summary */}
                <div style={{ marginBottom: "20px", padding: "16px", backgroundColor: "#f0fdf4", borderRadius: "8px", border: "1px solid #bbf7d0" }}>
                  <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
                    <div>
                      <span style={{ fontWeight: "600", color: "#065f46" }}>Total Items: </span>
                      <span>{billingList.length}</span>
                    </div>
                    <div>
                      <span style={{ fontWeight: "600", color: "#065f46" }}>Total Amount: </span>
                      <span>{billingCurrency} {billingList.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Table Header */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "50px 1fr 150px 60px",
                    gap: "12px",
                    padding: "12px 16px",
                    backgroundColor: "#f3f4f6",
                    borderRadius: "8px 8px 0 0",
                    fontWeight: "600",
                    fontSize: "13px",
                    color: "#374151",
                  }}
                >
                  <div>#</div>
                  <div>AWB Number</div>
                  <div>Amount ({billingCurrency})</div>
                  <div></div>
                </div>

                {/* Items List */}
                <div style={{ maxHeight: "400px", overflowY: "auto", border: "1px solid #e5e7eb", borderTop: "none", borderRadius: "0 0 8px 8px" }}>
                  {billingList.map((item, index) => (
                    <div
                      key={item.awb}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "50px 1fr 150px 60px",
                        gap: "12px",
                        padding: "12px 16px",
                        alignItems: "center",
                        borderBottom: index < billingList.length - 1 ? "1px solid #e5e7eb" : "none",
                        backgroundColor: index % 2 === 0 ? "white" : "#f9fafb",
                      }}
                    >
                      <span style={{ fontWeight: "500", color: "#6b7280", fontSize: "13px" }}>
                        {index + 1}
                      </span>
                      <span style={{ fontWeight: "600", color: "#3b82f6", fontSize: "14px" }}>
                        {item.awb}
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={item.amount}
                        onChange={(e) => updateBillingItemAmount(item.awb, e.target.value)}
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: "14px",
                          width: "100%",
                          textAlign: "right",
                        }}
                      />
                      <button
                        onClick={() => removeFromBillingList(item.awb)}
                        style={{
                          padding: "6px",
                          color: "#dc2626",
                          backgroundColor: "transparent",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                        title="Remove"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        /* Billing History View - Table Style */
        <div style={styles.card}>
          {/* Header Row - Title Left, Controls Right */}
          <div style={{ 
            padding: "16px 20px", 
            borderBottom: "1px solid #e5e7eb",
            backgroundColor: "#fafbfc",
            borderRadius: "12px 12px 0 0"
          }}>
            {/* Main Header Row */}
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center",
              flexWrap: "wrap",
              gap: "16px"
            }}>
              {/* Left Side - Title & Last Updated */}
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <h3 style={{ ...styles.cardTitle, margin: 0, fontSize: "18px" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: "8px", verticalAlign: "middle" }}>
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  Billing Batch History
                </h3>
                {lastHistoryUpdate && (
                  <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                    Last updated: {lastHistoryUpdate.toLocaleTimeString()}
                  </span>
                )}
              </div>
              
              {/* Right Side - All Filters & Buttons in One Row */}
              <div style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: "8px",
                flexWrap: "wrap",
                backgroundColor: "#fff",
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid #e5e7eb"
              }}>
                {/* From Date */}
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: "500" }}>From</span>
                  <input
                    type="date"
                    value={historyFromDate}
                    onChange={(e) => setHistoryFromDate(e.target.value)}
                    style={{
                      padding: "6px 8px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "12px",
                      color: "#374151",
                      backgroundColor: "#fff",
                      minWidth: "130px"
                    }}
                  />
                </div>
                
                {/* To Date */}
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: "500" }}>To</span>
                  <input
                    type="date"
                    value={historyToDate}
                    onChange={(e) => setHistoryToDate(e.target.value)}
                    style={{
                      padding: "6px 8px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "12px",
                      color: "#374151",
                      backgroundColor: "#fff",
                      minWidth: "130px"
                    }}
                  />
                </div>
                
                {/* Status Dropdown */}
                <select
                  value={historyStatusFilter}
                  onChange={(e) => setHistoryStatusFilter(e.target.value)}
                  style={{
                    padding: "6px 10px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "12px",
                    color: "#374151",
                    backgroundColor: "#fff",
                    minWidth: "100px",
                    cursor: "pointer"
                  }}
                >
                  <option value="">All Status</option>
                  <option value="PENDING">Pending</option>
                  <option value="PROCESSING">Processing</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="PARTIALLY_COMPLETED">Partial</option>
                  <option value="FAILED">Failed</option>
                </select>
                
                {/* Divider */}
                <div style={{ width: "1px", height: "24px", backgroundColor: "#e5e7eb" }} />
                
                {/* Action Buttons */}
                <button
                  onClick={() => fetchBatchHistory(historyFromDate, historyToDate, historyStatusFilter)}
                  disabled={batchHistoryLoading}
                  style={{
                    padding: "6px 14px",
                    border: "none",
                    borderRadius: "6px",
                    backgroundColor: "#3b82f6",
                    color: "#fff",
                    cursor: batchHistoryLoading ? "not-allowed" : "pointer",
                    fontSize: "12px",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    transition: "background-color 0.2s",
                    opacity: batchHistoryLoading ? 0.7 : 1
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                  </svg>
                  {batchHistoryLoading ? "Loading..." : "Apply"}
                </button>
                
                <button
                  onClick={() => {
                    setHistoryFromDate(getDefaultFromDate());
                    setHistoryToDate(getDefaultToDate());
                    setHistoryStatusFilter("");
                    fetchBatchHistory(getDefaultFromDate(), getDefaultToDate(), "");
                  }}
                  style={{
                    padding: "6px 14px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    backgroundColor: "#fff",
                    color: "#374151",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    transition: "background-color 0.2s"
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 4v6h6M23 20v-6h-6" />
                    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
                  </svg>
                  Reset
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div style={{ padding: "20px" }}>
            {batchHistoryLoading ? (
              <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <div style={{
                  width: "48px",
                  height: "48px",
                  margin: "0 auto 16px",
                  border: "4px solid #e5e7eb",
                  borderTop: "4px solid #3b82f6",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }} />
                <p style={{ color: "#6b7280", fontSize: "14px", fontWeight: "500" }}>Loading batch history...</p>
                <p style={{ color: "#9ca3af", fontSize: "12px", marginTop: "8px" }}>Please wait while we fetch your data</p>
              </div>
            ) : batchTaskHistory.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "#6b7280" }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ margin: "0 auto 16px", opacity: 0.5 }}>
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <p style={{ fontSize: "16px", marginBottom: "8px" }}>No batch tasks found</p>
                <p style={{ fontSize: "14px" }}>Batch uploads will appear here</p>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f9fafb" }}>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: "600", color: "#374151", borderBottom: "2px solid #e5e7eb" }}>Task ID</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: "600", color: "#374151", borderBottom: "2px solid #e5e7eb" }}>Status</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: "600", color: "#374151", borderBottom: "2px solid #e5e7eb" }}>Progress</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: "600", color: "#374151", borderBottom: "2px solid #e5e7eb" }}>Results</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: "600", color: "#374151", borderBottom: "2px solid #e5e7eb" }}>Created</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: "600", color: "#374151", borderBottom: "2px solid #e5e7eb" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchTaskHistory.map((task) => {
                      const totalItems = task.totalItems || 0;
                      const successCount = task.successCount || 0;
                      const failedCount = task.failureCount || task.failedCount || 0;
                      const progressPercent = totalItems > 0 ? Math.round(((successCount + failedCount) / totalItems) * 100) : 0;
                      
                      const statusConfig = {
                        COMPLETED: { icon: "✓", color: "#16a34a", bg: "#f0fdf4", text: "Completed" },
                        PARTIALLY_COMPLETED: { icon: "⚠", color: "#d97706", bg: "#fffbeb", text: "Partial" },
                        FAILED: { icon: "✕", color: "#dc2626", bg: "#fef2f2", text: "Failed" },
                        PROCESSING: { icon: "⟳", color: "#3b82f6", bg: "#eff6ff", text: "Processing" },
                        PENDING: { icon: "◯", color: "#6b7280", bg: "#f9fafb", text: "Pending" },
                        PAUSED: { icon: "⏸", color: "#d97706", bg: "#fffbeb", text: "Paused" },
                        CANCELLED: { icon: "⊘", color: "#6b7280", bg: "#f9fafb", text: "Cancelled" },
                      };
                      const config = statusConfig[task.status] || statusConfig.PENDING;
                      
                      return (
                        <tr key={task.taskId} style={{ borderBottom: "1px solid #e5e7eb" }}>
                          {/* Task ID */}
                          <td style={{ padding: "12px 16px", maxWidth: "200px" }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                              <span style={{
                                display: "inline-block",
                                backgroundColor: "#f1f5f9",
                                color: "#475569",
                                padding: "4px 8px",
                                borderRadius: "6px",
                                fontSize: "11px",
                                fontFamily: "monospace",
                                fontWeight: "600",
                                wordBreak: "break-all"
                              }}>
                                {task.taskId}
                              </span>
                              <span style={{ fontSize: "11px", color: "#6b7280" }}>
                                {task.description || task.sourceFileName || "-"}
                              </span>
                            </div>
                          </td>
                          
                          {/* Status */}
                          <td style={{ padding: "12px 16px" }}>
                            <span style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              padding: "4px 10px",
                              borderRadius: "20px",
                              fontSize: "12px",
                              fontWeight: "500",
                              color: config.color,
                              backgroundColor: config.bg,
                            }}>
                              {config.icon} {config.text}
                            </span>
                          </td>
                          
                          {/* Progress */}
                          <td style={{ padding: "12px 16px", minWidth: "140px" }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", fontWeight: "500" }}>
                                <span>{successCount + failedCount}/{totalItems}</span>
                                <span>{progressPercent}%</span>
                              </div>
                              <div style={{ height: "6px", backgroundColor: "#e5e7eb", borderRadius: "3px", overflow: "hidden" }}>
                                <div style={{ 
                                  height: "100%", 
                                  width: `${progressPercent}%`,
                                  backgroundColor: failedCount > 0 ? "#f59e0b" : "#10b981",
                                  borderRadius: "3px",
                                  transition: "width 0.3s"
                                }}></div>
                              </div>
                            </div>
                          </td>
                          
                          {/* Results */}
                          <td style={{ padding: "12px 16px" }}>
                            <div style={{ display: "flex", gap: "16px", fontSize: "13px" }}>
                              <span style={{ display: "flex", alignItems: "center", gap: "4px", color: "#10b981", fontWeight: "500" }} title="Success">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                                {successCount}
                              </span>
                              <span style={{ display: "flex", alignItems: "center", gap: "4px", color: failedCount > 0 ? "#ef4444" : "#9ca3af", fontWeight: "500" }} title="Failed">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                {failedCount}
                              </span>
                            </div>
                          </td>
                          
                          {/* Created Date */}
                          <td style={{ padding: "12px 16px", fontSize: "12px", color: "#6b7280" }}>
                            {new Date(task.createdAt).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </td>
                          
                          {/* Actions */}
                          <td style={{ padding: "12px 16px" }}>
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              <button
                                style={{
                                  padding: "6px 12px",
                                  backgroundColor: "#eff6ff",
                                  color: "#3b82f6",
                                  border: "none",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px"
                                }}
                                onClick={() => {
                                  viewBatchTaskDetails(task.taskId);
                                  setShowBulkUpdateModal(true);
                                }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                View
                              </button>
                              {["PARTIALLY_COMPLETED", "FAILED"].includes(task.status) && failedCount > 0 && (
                                <button
                                  onClick={async () => {
                                    try {
                                      await retryAllFailedItems(task.taskId);
                                      setBatchTaskId(task.taskId);
                                      setBulkUpdateStep("processing");
                                      setShowBulkUpdateModal(true);
                                    } catch (err) {
                                      setError("Failed to start retry");
                                    }
                                  }}
                                  style={{
                                    padding: "6px 12px",
                                    backgroundColor: "#fef3c7",
                                    color: "#b45309",
                                    border: "none",
                                    borderRadius: "4px",
                                    fontSize: "12px",
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px"
                                  }}
                                >
                                  Retry
                                </button>
                              )}
                              {task.status === "PROCESSING" && (
                                <button
                                  onClick={() => {
                                    setBatchTaskId(task.taskId);
                                    setBulkUpdateStep("processing");
                                    startProgressPolling(task.taskId);
                                    setShowBulkUpdateModal(true);
                                  }}
                                  style={{
                                    padding: "6px 12px",
                                    backgroundColor: "#eff6ff",
                                    color: "#3b82f6",
                                    border: "none",
                                    borderRadius: "4px",
                                    fontSize: "12px",
                                    cursor: "pointer"
                                  }}
                                >
                                  Progress
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {showBulkUpdateModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !bulkUpdateLoading) {
              resetBulkUpdateModal();
            }
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "600px",
              maxHeight: "90vh",
              overflow: "auto",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "600", color: "#111827" }}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#8b5cf6"
                  strokeWidth="2"
                  style={{ verticalAlign: "middle", marginRight: "10px" }}
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                Bulk Update Billing
              </h2>
              {!bulkUpdateLoading && (
                <button
                  onClick={resetBulkUpdateModal}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "24px",
                    cursor: "pointer",
                    color: "#6b7280",
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              )}
            </div>

            {/* Modal Body */}
            <div style={{ padding: "24px" }}>
              {/* Step 1: Upload */}
              {bulkUpdateStep === "upload" && (
                <div>
                  <p style={{ margin: "0 0 16px", color: "#6b7280", fontSize: "14px" }}>
                    Upload a CSV or Excel file containing AWB numbers (UB column) and billing amounts.
                  </p>

                  {/* Download Template Button */}
                  <div style={{ marginBottom: "16px" }}>
                    <button
                      onClick={downloadBulkTemplate}
                      style={{
                        padding: "10px 16px",
                        backgroundColor: "#f3f4f6",
                        color: "#374151",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontWeight: "500",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "13px",
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Download Template
                    </button>
                  </div>

                  <div
                    style={{
                      border: "2px dashed #d1d5db",
                      borderRadius: "8px",
                      padding: "40px 20px",
                      textAlign: "center",
                      backgroundColor: "#f9fafb",
                      cursor: "pointer",
                    }}
                    onClick={() => bulkUpdateFileInputRef.current?.click()}
                  >
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#9ca3af"
                      strokeWidth="1.5"
                      style={{ margin: "0 auto 16px" }}
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <p style={{ margin: "0 0 8px", fontWeight: "500", color: "#374151" }}>
                      Click to upload or drag and drop
                    </p>
                    <p style={{ margin: 0, fontSize: "13px", color: "#9ca3af" }}>
                      CSV, XLS, or XLSX files supported
                    </p>
                  </div>
                  <input
                    ref={bulkUpdateFileInputRef}
                    type="file"
                    accept=".csv,.xls,.xlsx"
                    style={{ display: "none" }}
                    onChange={handleBulkUpdateFileSelect}
                  />
                </div>
              )}

              {/* Step 2: Column Mapping */}
              {bulkUpdateStep === "mapping" && (
                <div>
                  <div
                    style={{
                      backgroundColor: "#f0fdf4",
                      border: "1px solid #86efac",
                      borderRadius: "8px",
                      padding: "12px 16px",
                      marginBottom: "20px",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    <div>
                      <span style={{ fontWeight: "500", color: "#166534" }}>
                        {bulkUpdateFile?.name}
                      </span>
                      <span style={{ color: "#15803d", marginLeft: "8px", fontSize: "13px" }}>
                        ({bulkUpdateFileData.length} rows found)
                      </span>
                    </div>
                  </div>

                  <p style={{ margin: "0 0 16px", color: "#374151", fontWeight: "500" }}>
                    Map your file columns to billing fields:
                  </p>

                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "6px",
                          fontWeight: "500",
                          color: "#374151",
                          fontSize: "14px",
                        }}
                      >
                        AWB Number <span style={{ color: "#dc2626" }}>*</span>
                      </label>
                      <select
                        value={bulkUpdateMapping.awb}
                        onChange={(e) =>
                          setBulkUpdateMapping((prev) => ({ ...prev, awb: e.target.value }))
                        }
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                      >
                        <option value="">-- Select Column --</option>
                        {bulkUpdateFileHeaders.map((header) => (
                          <option key={header.index} value={header.index}>
                            {header.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "6px",
                          fontWeight: "500",
                          color: "#374151",
                          fontSize: "14px",
                        }}
                      >
                        Billed Amount <span style={{ color: "#dc2626" }}>*</span>
                      </label>
                      <select
                        value={bulkUpdateMapping.amount}
                        onChange={(e) =>
                          setBulkUpdateMapping((prev) => ({ ...prev, amount: e.target.value }))
                        }
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                      >
                        <option value="">-- Select Column --</option>
                        {bulkUpdateFileHeaders.map((header) => (
                          <option key={header.index} value={header.index}>
                            {header.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "6px",
                          fontWeight: "500",
                          color: "#374151",
                          fontSize: "14px",
                        }}
                      >
                        Currency Column <span style={{ color: "#9ca3af", fontWeight: "400" }}>(Optional)</span>
                      </label>
                      <select
                        value={bulkUpdateMapping.currency}
                        onChange={(e) =>
                          setBulkUpdateMapping((prev) => ({ ...prev, currency: e.target.value }))
                        }
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                      >
                        <option value="">-- Use Default Currency --</option>
                        {bulkUpdateFileHeaders.map((header) => (
                          <option key={header.index} value={header.index}>
                            {header.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Default Currency - shown when no currency column mapped */}
                    {!bulkUpdateMapping.currency && (
                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "6px",
                            fontWeight: "500",
                            color: "#374151",
                            fontSize: "14px",
                          }}
                        >
                          Default Currency
                        </label>
                        <select
                          value={bulkUpdateDefaultCurrency}
                          onChange={(e) => setBulkUpdateDefaultCurrency(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "10px 12px",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            fontSize: "14px",
                          }}
                        >
                          <option value="AED">AED</option>
                          <option value="SAR">SAR</option>
                          <option value="USD">USD</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Preview */}
                  {bulkUpdateMapping.awb && bulkUpdateMapping.amount && (
                    <div style={{ marginTop: "20px" }}>
                      <p style={{ margin: "0 0 8px", fontWeight: "500", color: "#374151", fontSize: "14px" }}>
                        Preview (first 3 rows):
                      </p>
                      <div
                        style={{
                          backgroundColor: "#f9fafb",
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                          overflow: "hidden",
                        }}
                      >
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                          <thead>
                            <tr style={{ backgroundColor: "#f3f4f6" }}>
                              <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: "600" }}>
                                AWB
                              </th>
                              <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: "600" }}>
                                Amount
                              </th>
                              <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: "600" }}>
                                Currency
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {bulkUpdateFileData.slice(0, 3).map((row, idx) => (
                              <tr key={idx} style={{ borderTop: "1px solid #e5e7eb" }}>
                                <td style={{ padding: "10px 12px" }}>
                                  {row[parseInt(bulkUpdateMapping.awb)] || "-"}
                                </td>
                                <td style={{ padding: "10px 12px" }}>
                                  {row[parseInt(bulkUpdateMapping.amount)] || "-"}
                                </td>
                                <td style={{ padding: "10px 12px" }}>
                                  {bulkUpdateMapping.currency
                                    ? row[parseInt(bulkUpdateMapping.currency)] || bulkUpdateDefaultCurrency
                                    : bulkUpdateDefaultCurrency}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Additional Options */}
                  <div style={{ marginTop: "20px", padding: "16px", backgroundColor: "#f9fafb", borderRadius: "8px" }}>
                    <div style={{ marginBottom: "12px" }}>
                      <label style={{ display: "block", marginBottom: "6px", fontWeight: "500", color: "#374151", fontSize: "14px" }}>
                        Description (optional)
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., January 2026 Billing"
                        value={batchDescription}
                        onChange={(e) => setBatchDescription(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: "14px",
                        }}
                      />
                    </div>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={batchSendEmail}
                        onChange={(e) => setBatchSendEmail(e.target.checked)}
                        style={{ width: "16px", height: "16px", accentColor: "#8b5cf6" }}
                      />
                      <span style={{ fontSize: "14px", color: "#374151" }}>Send me email notification when complete</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Step 3: Processing */}
              {bulkUpdateStep === "processing" && batchCreating && (
                <div style={{ textAlign: "center", padding: "40px 20px" }}>
                  <div style={{
                    width: "64px",
                    height: "64px",
                    margin: "0 auto 20px",
                    border: "4px solid #e5e7eb",
                    borderTop: "4px solid #3b82f6",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }} />
                  <h3 style={{ margin: "0 0 12px", fontSize: "18px", fontWeight: "600", color: "#111827" }}>
                    {batchCreateProgress || "Loading data..."}
                  </h3>
                  <p style={{ margin: 0, fontSize: "14px", color: "#6b7280" }}>
                    Please wait while we set up your batch task
                  </p>
                  <div style={{
                    marginTop: "24px",
                    padding: "12px 20px",
                    backgroundColor: "#f0f9ff",
                    borderRadius: "8px",
                    display: "inline-block"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#0369a1", fontSize: "13px" }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 6v6l4 2" />
                      </svg>
                      This may take a moment...
                    </div>
                  </div>
                </div>
              )}
              
              {bulkUpdateStep === "processing" && !batchCreating && batchTaskProgress && (
                <div>
                  <div style={{ textAlign: "center", marginBottom: "24px" }}>
                    <div style={{
                      width: "64px",
                      height: "64px",
                      margin: "0 auto 16px",
                      border: "4px solid #e5e7eb",
                      borderTop: "4px solid #8b5cf6",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }} />
                    <h3 style={{ margin: "0 0 8px", fontSize: "18px", fontWeight: "600", color: "#111827" }}>
                      Processing Billing Update
                    </h3>
                    <p style={{ margin: 0, fontSize: "14px", color: "#6b7280" }}>
                      Task ID: {batchTaskProgress.taskId}
                    </p>
                  </div>

                  {/* Progress Bar */}
                  <div style={{ marginBottom: "20px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", fontSize: "13px", color: "#6b7280" }}>
                      <span>{batchTaskProgress.progressDisplay || "0/0 done"}</span>
                      <span>{batchTaskProgress.progressPercentage?.toFixed(1) || 0}%</span>
                    </div>
                    <div style={{ width: "100%", height: "12px", backgroundColor: "#e5e7eb", borderRadius: "6px", overflow: "hidden" }}>
                      <div
                        style={{
                          width: `${batchTaskProgress.progressPercentage || 0}%`,
                          height: "100%",
                          backgroundColor: "#8b5cf6",
                          borderRadius: "6px",
                          transition: "width 0.3s ease",
                        }}
                      />
                    </div>
                  </div>

                  {/* Stats */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" }}>
                    <div style={{ padding: "12px", backgroundColor: "#f0fdf4", borderRadius: "8px", textAlign: "center" }}>
                      <div style={{ fontSize: "24px", fontWeight: "700", color: "#16a34a" }}>
                        {batchTaskProgress.successCount || 0}
                      </div>
                      <div style={{ fontSize: "12px", color: "#15803d" }}>Successful</div>
                    </div>
                    <div style={{ padding: "12px", backgroundColor: batchTaskProgress.failureCount > 0 ? "#fef2f2" : "#f9fafb", borderRadius: "8px", textAlign: "center" }}>
                      <div style={{ fontSize: "24px", fontWeight: "700", color: batchTaskProgress.failureCount > 0 ? "#dc2626" : "#6b7280" }}>
                        {batchTaskProgress.failureCount || 0}
                      </div>
                      <div style={{ fontSize: "12px", color: batchTaskProgress.failureCount > 0 ? "#b91c1c" : "#6b7280" }}>Failed</div>
                    </div>
                  </div>

                  {/* Current Message */}
                  <div style={{ padding: "12px", backgroundColor: "#eff6ff", borderRadius: "8px", textAlign: "center" }}>
                    <p style={{ margin: 0, fontSize: "13px", color: "#1e40af" }}>
                      {batchTaskProgress.currentMessage || "Processing..."}
                    </p>
                    {batchTaskProgress.estimatedTimeRemaining && (
                      <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#3b82f6" }}>
                        Estimated time remaining: {batchTaskProgress.estimatedTimeRemaining}
                      </p>
                    )}
                  </div>

                  {/* Control Buttons */}
                  <div style={{ display: "flex", justifyContent: "center", gap: "12px", marginTop: "20px" }}>
                    {batchTaskProgress.status === "PROCESSING" && (
                      <>
                        <button
                          onClick={() => pauseBatchProcessing(batchTaskId)}
                          style={{
                            padding: "10px 20px",
                            backgroundColor: "#fef3c7",
                            color: "#b45309",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <rect x="6" y="4" width="4" height="16" />
                            <rect x="14" y="4" width="4" height="16" />
                          </svg>
                          Pause
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm("Are you sure you want to cancel this batch task?")) {
                              cancelBatchProcessing(batchTaskId);
                            }
                          }}
                          style={{
                            padding: "10px 20px",
                            backgroundColor: "#fee2e2",
                            color: "#dc2626",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontWeight: "500",
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                    {batchTaskProgress.status === "PAUSED" && (
                      <>
                        <button
                          onClick={() => resumeBatchProcessing(batchTaskId)}
                          style={{
                            padding: "10px 20px",
                            backgroundColor: "#d1fae5",
                            color: "#059669",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="5,3 19,12 5,21" />
                          </svg>
                          Resume
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm("Are you sure you want to cancel this batch task?")) {
                              cancelBatchProcessing(batchTaskId);
                            }
                          }}
                          style={{
                            padding: "10px 20px",
                            backgroundColor: "#fee2e2",
                            color: "#dc2626",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontWeight: "500",
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Step 4: Results */}
              {bulkUpdateStep === "results" && (batchTaskProgress || batchTaskDetails) && (
                <div>
                  {/* Status Header */}
                  {(() => {
                    const status = batchTaskDetails?.task?.status || batchTaskProgress?.status;
                    const statusConfig = {
                      COMPLETED: { icon: "✓", color: "#16a34a", bg: "#f0fdf4", border: "#86efac", text: "Billing Update Complete" },
                      PARTIALLY_COMPLETED: { icon: "⚠", color: "#d97706", bg: "#fffbeb", border: "#fcd34d", text: "Partially Complete" },
                      FAILED: { icon: "✗", color: "#dc2626", bg: "#fef2f2", border: "#fca5a5", text: "Update Failed" },
                      CANCELLED: { icon: "⊘", color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb", text: "Cancelled" },
                    };
                    const config = statusConfig[status] || statusConfig.COMPLETED;
                    
                    return (
                      <div style={{
                        backgroundColor: config.bg,
                        border: `1px solid ${config.border}`,
                        borderRadius: "8px",
                        padding: "16px",
                        marginBottom: "20px",
                        textAlign: "center",
                      }}>
                        <div style={{ fontSize: "32px", marginBottom: "8px" }}>{config.icon}</div>
                        <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600", color: config.color }}>
                          {config.text}
                        </h3>
                        {batchTaskId && (
                          <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#6b7280" }}>
                            Task ID: {batchTaskId}
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  {/* Stats Summary */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "20px" }}>
                    <div style={{ padding: "16px", backgroundColor: "#f9fafb", borderRadius: "8px", textAlign: "center" }}>
                      <div style={{ fontSize: "24px", fontWeight: "700", color: "#374151" }}>
                        {batchTaskDetails?.task?.totalItems || batchTaskProgress?.totalItems || 0}
                      </div>
                      <div style={{ fontSize: "12px", color: "#6b7280" }}>Total Items</div>
                    </div>
                    <div style={{ padding: "16px", backgroundColor: "#f0fdf4", borderRadius: "8px", textAlign: "center" }}>
                      <div style={{ fontSize: "24px", fontWeight: "700", color: "#16a34a" }}>
                        {batchTaskDetails?.task?.successCount || batchTaskProgress?.successCount || 0}
                      </div>
                      <div style={{ fontSize: "12px", color: "#15803d" }}>Successful</div>
                    </div>
                    <div style={{
                      padding: "16px",
                      backgroundColor: (batchTaskDetails?.task?.failureCount || batchTaskProgress?.failureCount) > 0 ? "#fef2f2" : "#f9fafb",
                      borderRadius: "8px",
                      textAlign: "center",
                    }}>
                      <div style={{
                        fontSize: "24px",
                        fontWeight: "700",
                        color: (batchTaskDetails?.task?.failureCount || batchTaskProgress?.failureCount) > 0 ? "#dc2626" : "#6b7280",
                      }}>
                        {batchTaskDetails?.task?.failureCount || batchTaskProgress?.failureCount || 0}
                      </div>
                      <div style={{ fontSize: "12px", color: (batchTaskDetails?.task?.failureCount || batchTaskProgress?.failureCount) > 0 ? "#b91c1c" : "#6b7280" }}>
                        Failed
                      </div>
                    </div>
                  </div>

                  {/* Failed Items List */}
                  {batchTaskDetails?.failedItems?.length > 0 && (
                    <div style={{ marginBottom: "16px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                        <p style={{ margin: 0, fontWeight: "500", color: "#374151", fontSize: "14px" }}>
                          Failed Items ({batchTaskDetails.failedItems.length}):
                        </p>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <button
                            onClick={() => retryAllFailedItems(batchTaskId)}
                            style={{
                              padding: "6px 12px",
                              backgroundColor: "#fef3c7",
                              color: "#b45309",
                              border: "none",
                              borderRadius: "6px",
                              fontSize: "13px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 4v6h6M23 20v-6h-6" />
                              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
                            </svg>
                            Retry All
                          </button>
                          <button
                            onClick={downloadBatchErrorReport}
                            style={{
                              padding: "6px 12px",
                              backgroundColor: "#fee2e2",
                              color: "#dc2626",
                              border: "none",
                              borderRadius: "6px",
                              fontSize: "13px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="7 10 12 15 17 10" />
                              <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            Download
                          </button>
                        </div>
                      </div>
                      <div style={{ maxHeight: "200px", overflowY: "auto", backgroundColor: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "6px" }}>
                        {batchTaskDetails.failedItems.slice(0, 10).map((item, idx) => (
                          <div
                            key={item.id || idx}
                            style={{
                              padding: "10px 12px",
                              borderBottom: idx < Math.min(batchTaskDetails.failedItems.length - 1, 9) ? "1px solid #fecaca" : "none",
                              fontSize: "13px",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div>
                              <span style={{ color: "#dc2626", fontWeight: "500" }}>{item.awbNumber}</span>
                              <span style={{ color: "#7f1d1d", marginLeft: "8px" }}>
                                {item.errorMessage || item.errorCode || "Failed"}
                              </span>
                            </div>
                            <button
                              onClick={async () => {
                                try {
                                  await retrySingleItem(item.id);
                                  const details = await getBatchDetails(batchTaskId);
                                  setBatchTaskDetails(details);
                                  setBatchTaskProgress(details.task);
                                  setSuccess(`Retried ${item.awbNumber}`);
                                } catch (err) {
                                  setError(`Failed to retry ${item.awbNumber}`);
                                }
                              }}
                              style={{
                                padding: "4px 8px",
                                backgroundColor: "#fef3c7",
                                color: "#b45309",
                                border: "none",
                                borderRadius: "4px",
                                fontSize: "12px",
                                cursor: "pointer",
                              }}
                            >
                              Retry
                            </button>
                          </div>
                        ))}
                        {batchTaskDetails.failedItems.length > 10 && (
                          <div style={{ padding: "10px 12px", fontSize: "13px", color: "#b91c1c" }}>
                            ... and {batchTaskDetails.failedItems.length - 10} more failed items
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Success Items Download */}
                  {batchTaskDetails?.successItems?.length > 0 && (
                    <div style={{ marginBottom: "16px" }}>
                      <button
                        onClick={downloadBatchSuccessReport}
                        style={{
                          padding: "10px 16px",
                          backgroundColor: "#f0fdf4",
                          color: "#16a34a",
                          border: "1px solid #86efac",
                          borderRadius: "6px",
                          fontSize: "14px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          width: "100%",
                          justifyContent: "center",
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Download Success Report ({batchTaskDetails.successItems.length} items)
                      </button>
                    </div>
                  )}
                </div>
              )}

            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: "16px 24px",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "12px",
              }}
            >
                {bulkUpdateStep === "upload" && (
                  <button
                    onClick={resetBulkUpdateModal}
                    style={{
                      padding: "10px 20px",
                      backgroundColor: "#f3f4f6",
                      color: "#374151",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontWeight: "500",
                    }}
                  >
                    Cancel
                  </button>
                )}

                {bulkUpdateStep === "mapping" && (
                  <>
                    <button
                      onClick={() => {
                        setBulkUpdateStep("upload");
                        setBulkUpdateFile(null);
                        setBulkUpdateFileHeaders([]);
                        setBulkUpdateFileData([]);
                        setBulkUpdateMapping({ awb: "", amount: "", currency: "" });
                        setBatchDescription("");
                        setBatchSendEmail(false);
                        if (bulkUpdateFileInputRef.current) {
                          bulkUpdateFileInputRef.current.value = "";
                        }
                      }}
                      style={{
                        padding: "10px 20px",
                        backgroundColor: "#f3f4f6",
                        color: "#374151",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontWeight: "500",
                      }}
                    >
                      Back
                    </button>
                    <button
                      onClick={processBatchUpdate}
                      disabled={!bulkUpdateMapping.awb || !bulkUpdateMapping.amount || bulkUpdateLoading}
                      style={{
                        padding: "10px 20px",
                        backgroundColor:
                          !bulkUpdateMapping.awb || !bulkUpdateMapping.amount || bulkUpdateLoading
                            ? "#d1d5db"
                            : "#8b5cf6",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor:
                          !bulkUpdateMapping.awb || !bulkUpdateMapping.amount || bulkUpdateLoading
                            ? "not-allowed"
                            : "pointer",
                        fontWeight: "500",
                      }}
                    >
                      {bulkUpdateLoading ? "Processing..." : `Submit ${bulkUpdateFileData.length} Items`}
                    </button>
                  </>
                )}

                {bulkUpdateStep === "processing" && (
                  <button
                    onClick={resetBulkUpdateModal}
                    disabled={batchTaskProgress?.status === "PROCESSING"}
                    style={{
                      padding: "10px 20px",
                      backgroundColor: batchTaskProgress?.status === "PROCESSING" ? "#d1d5db" : "#f3f4f6",
                      color: "#374151",
                      border: "none",
                      borderRadius: "6px",
                      cursor: batchTaskProgress?.status === "PROCESSING" ? "not-allowed" : "pointer",
                      fontWeight: "500",
                    }}
                  >
                    Close
                  </button>
                )}

                {bulkUpdateStep === "results" && (
                  <>
                    <button
                      onClick={() => {
                        setBulkUpdateStep("upload");
                        setBulkUpdateFile(null);
                        setBulkUpdateFileHeaders([]);
                        setBulkUpdateFileData([]);
                        setBulkUpdateMapping({ awb: "", amount: "", currency: "" });
                        setBatchDescription("");
                        setBatchSendEmail(false);
                        setBatchTaskId(null);
                        setBatchTaskProgress(null);
                        setBatchTaskDetails(null);
                        if (bulkUpdateFileInputRef.current) {
                          bulkUpdateFileInputRef.current.value = "";
                        }
                      }}
                      style={{
                        padding: "10px 20px",
                        backgroundColor: "#f3f4f6",
                        color: "#374151",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontWeight: "500",
                      }}
                    >
                      New Upload
                    </button>
                    <button
                      onClick={resetBulkUpdateModal}
                      style={{
                        padding: "10px 20px",
                        backgroundColor: "#8b5cf6",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontWeight: "500",
                      }}
                    >
                      Done
                    </button>
                  </>
                )}

            </div>
          </div>
        </div>
      )}
    </div>
  );
}
